// Rome's Colosseum look (the registry's `looks`; rome.js, the Trevi Fountain, is the city's first), "Summer night by the
// Colosseum" (August, the night of San Lorenzo, when Romans watch for shooting stars). A cobbled lane in
// Monti runs down to the floodlit Colosseum: three arcaded tiers glowing from inside, the attic with its small windows,
// and the broken outer ring stepping down to the lower inner ring. Two umbrella pines stand at its feet and a Romanesque
// bell tower behind it, gulls circle the floodlights and Perseids streak across the stars. Ochre and terracotta houses with
// green shutters line the lane: a trattoria with checked cloths and candles, laundry lines, a corner shrine, a nasone that
// never stops trickling, a parked scooter, cats, and a garden wall spilling bougainvillea whose magenta bracts tumble
// down at three depths. An accordion player busks by the trattoria all night; a couple stroll up the lane arm in arm to the
// Colosseum, and a signora walks her terrier out of the garden gate and back.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, inEvent, cycle, hash, rng, bayer, hex,
  mix, makeCanvas, rect, makeHalo, drawHalo, text, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
// The Colosseum is projected as an ellipse seen in perspective: x = cx + F·a·cosφ / z, z = D + b·sinφ (φ in (π, 2π) is
// the near half), and a wall height h (0..hw) lands on screen at y = eyeY − (h − eyeH)·F / z. The outer ring stands for
// φ up to `cut`; past it only the lower inner ring (a2, b2) is left.
const CO = { cx: 232, F: 250, D: 3.5, b: 0.82, a2: 0.86, b2: 0.7, hw: 0.75, eyeY: 175.5, eyeH: 0.2625, cut: 65 * TAU / 80, base: 199 };
const NBAYS = 80, DPHI = TAU / NBAYS;                 // 80 arches all round
const VP = { x: 236, y: 176 };                        // eye-level vanishing point of the lane's horizontal lines
const GY = 186;                                       // the lane runs downhill: its surface converges a bit lower
const L1 = { r: 96, side: 116, top: 12, base: 252 };  // trattoria house: front x 0..95, side face 96..115
const R1 = { l: 384, side: 364, top: 0, base: 252 };  // garden house: front x 384..479, side face 364..383
const L2 = { l: 116, r: 152, side: 160, top: 94, base: 230 };   // further down the lane (front 116..151, side ..159)
const R2 = { l: 306, r: 364, side: 300, top: 112, base: 226 };  // the house under restoration (front 306..363)
const ROAD = { y0: 199, y1: 208 };                    // the street at the Colosseum's feet, below the lane's last step
const BB = { x: 312, y: 118, w: 48, h: 20 };          // the gelato hoarding on R2's scaffold (the site paints its pitch here)
const BB_LAMPS = [10, 38];                            // its two floodlamps (x offsets), on arms above the face
const LANT_L = { x: 108, y: 166 };                    // wall lanterns: on the trattoria's side face,
const LANT_R = { x: 378, y: 160 };                    // on the garden house's corner,
const LANT_M = { x: 156, y: 178 };                    // and a small one down the lane
const CAMP = { x: 289, top: 117 };                    // the Romanesque bell tower on the Celio, behind the Colosseum
const NAS = { x: 368, y: 224 };                       // the nasone (street fountain): body x 368..374, spout at x 363
const VESPA = { x: 398, y: 226 };                     // parked scooter sprite top-left
const TABLES = [{ x: 22, y: 234, w: 22 }, { x: 60, y: 236, w: 22 }, { x: 104, y: 240, w: 24 }];

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** a sprite from rows of palette keys ('.' = transparent) */
function sprite(rows, pal) {
  const p = pix(rows[0].length, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') p.set(i, j, pal[row[i]]); });
  return p.done();
}
/** a left-right mirror of a canvas */
function mirrored(c) {
  const [m, mx] = makeCanvas(c.width, c.height);
  mx.translate(c.width, 0); mx.scale(-1, 1); mx.drawImage(c, 0, 0);
  return m;
}
/** FONT3 text into a pix buffer */
function textP(p, s, x, y, col, sc = 1) {
  let cx = x;
  for (const ch of s) { const g = FONT3[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.fill(cx + i * sc, y + j * sc, sc, sc, col); }); cx += 4 * sc; }
}
/** flattened dithered ellipse of light (pools on the cobbles), drawn additively */
function makePool(rx, ry, col, maxA) {
  const w = rx * 2 + 1, h = ry * 2 + 1, p = pix(w, h), c = hex(col);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
/** a flicker level: 1, with stutters inside the event windows */
function flick(t, ev, salt) {
  if (!inEvent(t, ev)) return 1;
  const hv = hash(salt, Math.floor(t * 20), 3);
  return hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.1;
}
/** y on a receding (side) face: the row that is at y0 on the face's near edge x0, at column x */
const recede = (y0, x0, x) => VP.y + (y0 - VP.y) * (VP.x - x) / (VP.x - x0);

/* ================= Sky: warm August night, a faint Milky Way ================= */
const SKY = ['#060820', '#090c28', '#0d1030', '#111438', '#161840', '#1c1b48', '#241e4e', '#2e2254', '#3a2658', '#472a5a',
  '#56305c', '#66365c', '#783d5a', '#8a4658', '#9c5056', '#ac5c54', '#ba6a52', '#c67a54'];
// the band runs from the top middle down to the right (Sagittarius low in the south-west, Cygnus overhead)
const mwDist = (x, y) => ((y - 0) * 0.83 - (x - 176) * 0.56);     // signed distance (px) across the band's axis
function milky(x, y) {
  const d = mwDist(x, y), core = Math.exp(-(d * d) / (2 * 15 * 15));
  const dust = Math.exp(-((d - 3) ** 2) / (2 * 3.2 * 3.2)) * (0.5 + 0.5 * vnoise(x / 9, y / 9, 44));
  const clumps = 0.55 + 0.9 * vnoise(x / 14, y / 14, 45) * vnoise(x / 5, y / 5, 46);
  return Math.max(0, core * clumps - dust * 0.55) * smooth(160, 40, y);
}
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 206), 1.45) * 0.78;
    v += Math.max(0, 1 - Math.hypot((i - CO.cx) / 190, (y - 196) / 86)) * 0.26;     // the floodlit Colosseum's glow
    v += milky(i, y) * 0.26;
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(31);
  for (let k = 0; k < 900 && STARS.length < 110; k++) {
    const x = 112 + Math.floor(r() * 256), y = 1 + Math.floor(r() * 150);
    if (y > 96 && (x < 158 || x > 306)) continue;                 // behind the lane's houses
    if (y > 118 && x > 150 && x < 320) continue;                  // too low: the glow washes them out
    const m = milky(x, y);
    if (r() > 0.35 + m * 2.2) continue;                           // denser along the Milky Way
    const bright = r() < 0.09;
    STARS.push({ x, y, k: 20 + Math.floor(r() * 60), ph: r(), b: bright ? 0.85 : 0.22 + r() * 0.45, bright, col: r() < 0.25 ? '#ffe2c0' : r() < 0.5 ? '#cfe0ff' : '#f0eeff' });
  }
}
/* thin warm streaks of cloud low over the city, scrolling slowly */
function renderClouds() {
  const h = 34, p = pix(W, h), r = rng(57), puffs = [];
  for (let k = 0; k < 7; k++) puffs.push({ x: r() * W, y: 8 + r() * 20, rx: 22 + r() * 40, ry: 1.6 + r() * 1.8 });
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = -9;
    for (const q of puffs) { let dx = x - q.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx / q.rx) ** 2 - ((y - q.y) / q.ry) ** 2); }
    v += 0.25 * Math.sin(TAU * 7 * x / W + y * 0.9) * Math.sin(TAU * 3 * x / W);
    if (v <= bayer(x, y) * 0.5) continue;
    p.set(x, y, v > 0.6 ? '#8a4a5e' : y > 20 ? '#a8605e' : '#6e3e5c');
  }
  return p.done();
}

/* ================= Perseids: a few shooting stars per loop, radiating from the upper left ================= */
const RADIANT = { x: 40, y: -90 };
const METEORS = [
  { t: 11.4, x: 190, y: 16, len: 46, dur: 0.8 }, { t: 43.8, x: 262, y: 8, len: 38, dur: 0.7 },
  { t: 79.2, x: 150, y: 34, len: 30, dur: 0.6 }, { t: 108.6, x: 226, y: 22, len: 58, dur: 1.0 },
  { t: 146.3, x: 300, y: 14, len: 34, dur: 0.65 }, { t: 181.7, x: 172, y: 6, len: 52, dur: 0.9 },
  { t: 219.5, x: 244, y: 40, len: 36, dur: 0.7 },
].map(m => { const dx = m.x - RADIANT.x, dy = m.y - RADIANT.y, d = Math.hypot(dx, dy); return { ...m, ux: dx / d, uy: dy / d }; });
function drawMeteors(t) {
  for (const m of METEORS) {
    const s = (t - m.t) / m.dur;
    if (s < 0 || s >= 1) continue;
    const a = Math.pow(Math.sin(Math.PI * Math.min(1, s * 1.15)), 0.7) * (s > 0.7 ? 1 + (s - 0.7) * 1.5 : 1);
    const head = m.len * s, tail = Math.min(head, 6 + m.len * 0.45);
    for (let k = 0; k <= tail; k++) {
      const d = head - k, x = Math.round(m.x + m.ux * d), y = Math.round(m.y + m.uy * d), f = 1 - k / (tail + 1);
      ctx.globalAlpha = Math.min(1, a * f * f * 1.1);
      ctx.fillStyle = k < 2 ? '#ffffff' : k < 6 ? '#e2f0ff' : '#9ab4ff';
      ctx.fillRect(x, y, 1, 1);
    }
    ctx.globalAlpha = 1;
    halo(HL.meteor, m.x + m.ux * head, m.y + m.uy * head, a * 0.9);
  }
}
/** a jet high up: red/green nav lights and a white strobe */
const PLANE = { t0: 56, t1: 136, x0: 104, x1: 372, y0: 30, y1: 22 };
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const s = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(PLANE.x0, PLANE.x1, s)), y = Math.round(lerp(PLANE.y0, PLANE.y1, s));
  rect(ctx, x - 2, y, 5, 1, '#2a2650'); rect(ctx, x, y - 1, 1, 1, '#2a2650');
  rect(ctx, x + 2, y, 1, 1, '#ff4a4a'); rect(ctx, x - 2, y + 1, 1, 1, '#4aff8a');
  const f = frac(t);
  if (f < 0.07 || (f > 0.17 && f < 0.22)) { rect(ctx, x, y - 1, 1, 1, '#ffffff'); halo(HL.strobe, x, y - 1, 0.9); }
}
/** gulls circling in the floodlight over the Colosseum (lit from below, whole laps per loop) */
const GULLS = [
  { k: 5, ph: 0.1, cx: 226, cy: 104, rx: 58, ry: 12, dir: 1 }, { k: 6, ph: 0.55, cx: 244, cy: 94, rx: 44, ry: 9, dir: -1 },
  { k: 4, ph: 0.8, cx: 214, cy: 116, rx: 70, ry: 8, dir: 1 },
];
const GULL_F = [['#...#', '.#.#.', '..#..'], ['##.##', '..#..'], ['..#..', '.#.#.', '#...#']];
function drawGulls(t) {
  GULLS.forEach((g, i) => {
    const a = TAU * (g.k * t / LOOP + g.ph) * g.dir;
    const x = Math.round(g.cx + Math.cos(a) * g.rx), y = Math.round(g.cy + Math.sin(a) * g.ry + wave(t, 31 + i * 7, i * 0.3) * 2);
    const flap = frac(t * 1.6 + i * 0.37), fr = flap < 0.55 ? 1 : flap < 0.7 ? 0 : flap < 0.85 ? 2 : 1;
    const warm = clamp((y - 86) / 34, 0, 1);
    ctx.fillStyle = warm > 0.5 ? '#ffe6c4' : '#e8e0e8';
    GULL_F[fr].forEach((row, j) => { for (let q = 0; q < 5; q++) if (row[q] === '#') ctx.fillRect(x - 2 + q, y - 1 + j, 1, 1); });
  });
}

/* ================= The Celio beyond: a hazy ridge of trees and roofs ================= */
function renderFar() {
  const p = pix(W, H), wins = [];
  for (let x = 110; x < 372; x++) {
    const ridge = 172 + Math.round(4 * Math.sin(x * 0.045 + 1) + 2.5 * Math.sin(x * 0.13) + (vnoise(x / 4, 0, 51) - 0.5) * 5);
    for (let y = ridge; y < 204; y++) {
      const v = 0.35 + (y - ridge) / 40 * 0.4 + (hash(x, y, 52) - 0.5) * 0.15;
      p.set(x, y, pick(['#2a1c3a', '#302040', '#382446', '#40284a'], v, x, y));
    }
    p.set(x, ridge, '#4a3050');
  }
  const r = rng(53);
  for (let k = 0; k < 18; k++) { const x = 112 + Math.floor(r() * 258), y = 184 + Math.floor(r() * 14); wins.push({ id: k, x, y, n: 2 + Math.floor(r() * 5), off: r() }); }
  return { c: trim(p), wins };
}

/* ================= The bell tower of Santi Giovanni e Paolo (far, hazy, dimly floodlit) ================= */
function renderCampanile() {
  const p = pix(W, H), X = CAMP.x, T = CAMP.top;
  for (let j = 0; j < 5; j++) for (let i = -j; i <= j; i++) if (Math.abs(i) <= 3) p.set(X + i, T + j, i < 0 ? '#7a4238' : i === 0 ? '#8a4a3c' : '#4a2a30');   // pyramid roof
  for (let i = -4; i <= 4; i++) p.set(X + i, T + 5, i < 1 ? '#8a5446' : '#503038');
  for (let y = T + 6; y < 176; y++) for (let i = -3; i <= 3; i++) {
    const st = (y - T - 6) % 6;                                     // storeys of 6 rows, a cornice on each
    let col = i < -1 ? '#7e4a42' : i < 1 ? '#6a3e3c' : '#4e2e34';
    if (st === 0) col = i < 1 ? '#946050' : '#5a3a3c';
    else if (st === 1) col = '#301c26';
    else if ((st === 3 || st === 4) && (i === -1 || i === 1)) col = y < T + 12 ? '#e89a50' : '#2a1822';   // bifore, the belfry lit
    p.set(X + i, y, col);
  }
  return trim(p);
}
const BELLS = [62, 65.2, 68.4, 182, 185.2, 188.4];     // the tower tolls three strokes, twice per loop
function drawCampanile(t) {
  blit(CAMP_C);
  let swing = 0, glow = 0;
  for (const b of BELLS) { const d = t - b + 0.3; if (d >= 0 && d < 2.6) { swing = Math.sin(d * 5) * (1 - d / 2.6); glow = Math.max(glow, 1 - d / 2.6); } }
  // the bells in the two belfry openings rock as they strike
  rect(ctx, CAMP.x - 1, CAMP.top + (swing > 0.3 ? 9 : 10), 1, 1, '#5a2c14');
  rect(ctx, CAMP.x + 1, CAMP.top + (swing < -0.3 ? 9 : 10), 1, 1, '#5a2c14');
  halo(HL.belfry, CAMP.x, CAMP.top + 10, 0.45 + 0.55 * glow);
}

/* ================= Umbrella pines (stone pines) on the grounds at the Colosseum's feet ================= */
// Two pines stand inside the fence in front of the monument's two ends: bare, slightly leaning trunks that cross the glowing
// arcades, limbs fanning out like an umbrella's ribs, and a flat, wide crown about a third of the tree's height. The left
// crown sits over the receding third tier, well under the attic rim; the right one is out in the sky past the broken edge.
// x: the trunk's foot, lean: the crown's offset from it (px), top: the crown's top row, w × h: the crown, lamp: the side
// the street lamp lights the trunk from.
const PINES = [
  { x: 186, lean: -6, top: 152, w: 34, h: 9, seed: 1, lamp: -1 },
  { x: 277, lean: 7, top: 140, w: 34, h: 10, seed: 4, lamp: 1 },
];
const PINE_FOOT = 196;                                // the grounds behind the fence (the fence and pavement cover the foot)
const PINE = ['#060e0e', '#0a1513', '#0f1c18', '#15241e', '#1c2e25', '#283a2d', '#34483a'];
const BARK = ['#120c10', '#1e1418', '#3a2420', '#6a3e2a', '#9a6036'];
function drawPine(p, q) {
  const r = rng(700 + q.seed), tufts = [];
  const cx = q.x + q.lean, top = q.top, bot = q.top + q.h, forkY = bot + 3;
  // trunk: dark bark with a lamp-lit edge, leaning and gently bent, flaring at the foot
  const trunkX = u => q.x + q.lean * u * u + Math.sin(u * Math.PI) * 1.4 * Math.sign(q.lean);
  for (let y = Math.round(forkY); y < PINE_FOOT; y++) {
    const u = (PINE_FOOT - y) / (PINE_FOOT - forkY), x = Math.round(trunkX(u)), lit = clamp(1 - u * 0.8, 0.3, 1);
    p.set(x, y, BARK[1]); p.set(x - q.lamp, y, BARK[0]);
    p.set(x + q.lamp, y, pick(BARK, 0.45 + lit * 0.5, x, y));
    if (u < 0.35) p.set(x - 2 * q.lamp, y, BARK[0]);                               // the foot flares
  }
  // limbs: four ribs from the fork up to the crown's underside
  for (const f of [-0.42, -0.16, 0.14, 0.4]) {
    const tx = cx + f * q.w, n = Math.max(Math.abs(tx - trunkX(1)), forkY - bot + 1) | 0;
    for (let k = 0; k <= n; k++) p.set(lerp(trunkX(1), tx, k / n), lerp(forkY, bot - 1, Math.pow(k / n, 0.6)), k < n * 0.5 ? BARK[1] : BARK[0]);
  }
  // crown: rows of rounded needle tufts, narrower on top (flat, domed shoulders), lumpy underneath
  const rows = Math.max(2, Math.round(q.h / 4.5));
  for (let ri = 0; ri < rows; ri++) {
    const f = ri / (rows - 1), n = Math.round(q.w / 5.5) + ri, span = q.w * (0.72 + 0.28 * Math.sin(Math.PI * (0.35 + 0.65 * f)));
    for (let k = 0; k < n; k++) {
      const u = (k + 0.5 + (r() - 0.5) * 0.5) / n - 0.5;
      tufts.push({ x: cx + u * span, y: top + 2.4 + f * (q.h - 5) + (u * 2) ** 2 * 2.2 + (r() - 0.5) * 1.2, rx: 3 + r() * 2.8, ry: 1.9 + r() * 1.1 });
    }
  }
  const best = (x, y) => { let v = 0, bt = null; for (const t of tufts) { const w = 1 - ((x - t.x) / t.rx) ** 2 - ((y - t.y) / t.ry) ** 2; if (w > v) { v = w; bt = t; } } return bt; };
  for (let y = Math.floor(top - 2); y <= Math.ceil(bot + 4); y++) for (let x = Math.floor(cx - q.w / 2 - 6); x <= Math.ceil(cx + q.w / 2 + 6); x++) {
    const t = best(x, y);
    if (!t || (hash(x, y, 80 + q.seed) < 0.12 && (!best(x, y - 1) || !best(x, y + 1)))) continue;   // ragged outline, no holes
    const dy = (y - t.y) / t.ry;
    let lv = 0.42 - dy * 0.22 + (vnoise(x / 1.7, y / 1.4, 70 + q.seed) - 0.5) * 0.35;
    if (!best(x, y - 1)) lv = 0.95;                                 // cool rim from the sky on top of each tuft
    p.set(x, y, pick(PINE, lv, x, y));
    if (!best(x, y + 1)) {                                          // warm rim: the Colosseum's floodlight from below
      const near = clamp(1 - Math.abs(x - CO.cx) / 110, 0, 1) * clamp((y - 112) / 36, 0.25, 1);
      if (hash(x, y, 93) < 0.35 + near * 0.65) p.set(x, y, near > 0.5 ? '#8a4a26' : near > 0.28 ? '#5a3224' : '#2e2426');
    }
  }
}
function renderPines(list) { const p = pix(W, H); for (const q of list) drawPine(p, q); return trim(p); }

/* ================= The Colosseum (floodlit travertine, arches glowing from inside) ================= */
const TRAV = ['#2e1a18', '#4c2a1e', '#6e3e24', '#94562a', '#b87034', '#d48e42', '#e8ac56', '#f6c872', '#fde29c', '#fff6cc'];
const BRICK = ['#2a1214', '#421a18', '#5e241c', '#7e3222', '#9c4228', '#b85630', '#cc6e3c'];
const GLOW = ['#3a1410', '#5e200e', '#8a3212', '#b84a18', '#e06a22', '#ff8e34', '#ffb85a', '#ffdc92'];
// three arcaded storeys (Doric, Ionic, Corinthian) in wall-height units: arch zone [a0, a1), entablature [e0, e1)
const TIERS = [
  { a0: 0.024, a1: 0.152, e0: 0.152, e1: 0.18, glow: 0.5 },
  { a0: 0.198, a1: 0.318, e0: 0.318, e1: 0.345, glow: 0.46 },
  { a0: 0.363, a1: 0.483, e0: 0.483, e1: 0.51, glow: 0.42 },
];
const INNER_TIERS = [{ a0: 0.024, a1: 0.148, e0: 0.148, e1: 0.172, glow: 0.48 }, { a0: 0.19, a1: 0.298, e0: 0.298, e1: 0.318, glow: 0.42 }];
const ATTIC = { b: 0.51, w0: 0.578, w1: 0.628, c0: 0.7, c1: 0.716, k0: 0.728 };
const NB_BUTT = 4;                                    // bays of the stepped buttress before the break
function coProj(phi, a, b) { const z = CO.D + b * Math.sin(phi); return [CO.cx + CO.F * a * Math.cos(phi) / z, CO.F / z]; }
const coY = (h, s) => CO.eyeY - (h - CO.eyeH) * s;
/** the screen columns of one ring for phi in [phiA, phiB]: [{ x, phi, s, bay, c, w }], c = column inside its arch bay of w */
function ringColumns(a, b, phiA, phiB) {
  const out = [], tan = Math.asin(b / CO.D), lo = Math.max(phiA, Math.PI + tan), hi = Math.min(phiB, TAU - tan);
  for (let k = Math.floor(lo / DPHI); k < Math.ceil(hi / DPHI); k++) {
    const pa = Math.max(lo, k * DPHI), pb = Math.min(hi, (k + 1) * DPHI);
    if (pb <= pa) continue;
    const xa = Math.round(coProj(pa, a, b)[0]), xb = Math.round(coProj(pb, a, b)[0]);
    for (let x = xa; x < xb; x++) {
      let l = pa, r = pb;
      for (let it = 0; it < 24; it++) { const m = (l + r) / 2; if (coProj(m, a, b)[0] < x + 0.5) l = m; else r = m; }
      const phi = (l + r) / 2;
      out.push({ x, phi, s: coProj(phi, a, b)[1], bay: ((k % NBAYS) + NBAYS) % NBAYS, c: x - xa, w: xb - xa });
    }
  }
  return out;
}
/** paint one column of a ring: travertine lit from below, arches glowing, the attic, bricked-up bays on the buttress */
function paintColumn(p, col, topH, tiers, opt) {
  const { x, s, c, w, bay } = col, facing = -Math.sin(col.phi), px1 = 1 / s;
  const yTop = Math.round(coY(topH, s));
  const pw = w >= 9 ? 3 : w >= 5 ? 2 : 1, ow = w - pw;
  for (let y = yTop; y <= CO.base; y++) {
    const h = CO.eyeH + (CO.eyeY - (y + 0.5)) / s;
    const face = 0.22 + 0.78 * Math.pow(Math.max(0, facing), 1.5);             // the curve turns away towards the ends
    let L = 0.08 + 0.8 * face * (1 - 0.3 * h / CO.hw);
    L *= opt.dim;
    let ramp = opt.brick && h > 0.18 ? BRICK : TRAV, glowV = -1;
    if (h >= ATTIC.b && opt.attic) {                                            // attic: pilasters, small windows, consoles
      if (c === 0) L += 0.07;
      if (h < ATTIC.b + 1.4 * px1) L -= 0.2;                                    // shadow above the third cornice
      if (bay % 2 === 1 && h >= ATTIC.w0 && h < ATTIC.w1 && w >= 4 && c >= Math.ceil(w * 0.35) && c <= Math.floor(w * 0.62)) {
        p.set(x, y, hash(bay, 9, 4) < 0.3 ? '#6a2c16' : '#2a1410'); continue;
      }
      if (h >= ATTIC.c0 && h < ATTIC.c1 && c === (w >> 1)) L -= 0.28;           // sockets of the awning masts
      if (h >= ATTIC.k0) L += h < ATTIC.k0 + px1 ? 0.18 : -0.02;                // crowning cornice, lit from below
    } else {
      let k = 0;
      while (k < tiers.length - 1 && h >= tiers[k].e1) k++;
      const T = tiers[k], prevE1 = k ? tiers[k - 1].e1 : 0.03;
      if (h < 0.03) L -= 0.08;                                                  // plinth
      else if (h >= T.e0) {                                                     // entablature band
        if (T.e1 - h < px1 * 1.1) L += 0.24;                                    // cornice lip
        else if (h - T.e0 < px1) L += 0.1;
        else L -= 0.06;
      } else if (h < T.a0) {                                                    // pedestal above the cornice below
        L += h - prevE1 < px1 * 1.2 ? -0.3 : -0.05;
      } else {                                                                  // arcade
        const yA1 = Math.round(coY(T.a1, s)), yA0 = Math.round(coY(T.a0, s));
        let open = c >= pw && y >= yA1 && y < yA0 && w >= 2;
        if (open && y === yA1 && ow >= 3 && (c === pw || c === w - 1)) open = false;   // rounded arch head
        if (open) {
          if (opt.bricked) { ramp = BRICK; L = L * 0.62 - (y & 1 ? 0.06 : 0); if (c === pw || y === yA1) L -= 0.1; }
          else {
            glowV = (T.glow + 0.2 * Math.pow(1 - (y - yA1) / Math.max(1, yA0 - yA1), 2)) * (0.6 + 0.4 * facing);
            if (ow <= 1) glowV -= 0.12;
            else if (c === pw || c === w - 1) glowV -= 0.08;
            const lit = hash(bay, k + (opt.inner ? 7 : 0), 11);
            glowV += lit < 0.1 ? -0.32 : lit > 0.88 ? 0.1 : 0;
          }
        } else {
          if (c === 0) L += 0.09; else if (c === pw - 1 && pw > 1) L -= 0.07;  // engaged half-columns
          if (c >= pw && y === yA1 - 1) L += 0.06;                              // voussoirs over the arch
          if (k === 0 && c < pw) L += 0.1 * (1 - (h - T.a0) / (T.a1 - T.a0));   // uplighters at the pier feet
          if (w <= 1) L -= 0.1;
        }
      }
    }
    if (glowV >= 0) { p.set(x, y, pick(GLOW, glowV, x, y)); continue; }
    const hv = hash(x, y, 7);                                                   // clamp holes and stains in the stone
    if (hv < 0.06) L -= 0.16; else if (hv < 0.11) L -= 0.06;
    if (y === yTop) L += 0.04;
    if (opt.ragged && y <= yTop + 1 && hash(x, y, 8) < 0.35) continue;
    if (opt.attic && topH >= CO.hw && y === yTop && hash(x, 0, 12) < 0.22) continue;   // the weathered crown
    p.set(x, y, pick(ramp, L, x, y));
  }
}
function renderColosseum() {
  const p = pix(W, H);
  const outer = ringColumns(1, CO.b, Math.PI, CO.cut);
  const xCut = outer[outer.length - 1].x;
  for (const col of outer) {
    const fromCut = (CO.cut - col.phi) / DPHI, butt = fromCut < NB_BUTT;
    let topH = CO.hw;
    if (butt) topH = CO.hw * (0.52 + 0.48 * Math.ceil((fromCut / NB_BUTT) * 7) / 7);   // the stepped buttress
    paintColumn(p, col, topH, TIERS, { attic: true, brick: butt && fromCut < 3, bricked: butt && Math.floor(fromCut) < 3, dim: butt ? 0.92 : 1 });
  }
  for (let y = Math.round(coY(CO.hw * 0.55, outer[outer.length - 1].s)); y <= CO.base; y++) p.set(xCut, y, y & 1 ? '#3a1a16' : '#4a221a');   // the break
  const inner = ringColumns(CO.a2, CO.b2, Math.PI * 1.3, TAU).filter(c => c.x > xCut);
  for (const col of inner) {
    const after = (col.phi - CO.cut) / DPHI;
    const topH = 0.33 + 0.05 * hash(col.bay, 3, 5) + 0.07 * Math.max(0, 1 - after / 3);
    paintColumn(p, col, topH, INNER_TIERS, { attic: false, brick: true, bricked: false, dim: 0.8, inner: true, ragged: true });
  }
  return { ...trim(p), x0: outer[0].x, x1: inner.length ? inner[inner.length - 1].x : xCut, xCut };
}
function drawColosseum(t) {
  const br = 0.94 + 0.06 * wave(t, 6, 0.2);
  halo(HL.colo, CO.cx, 170, br); halo(HL.coloMid, CO.cx - 8, 160, br);
  blit(COLO);
  halo(HL.coloBase, CO.cx, 197, 0.9 * br);
}

/* ================= The street at the Colosseum's feet: fence, lamp posts, traffic, strollers ================= */
const POSTS = [176, 288];                              // tall street lamps in front of the monument
function renderRoad() {
  const p = pix(W, H);
  for (let x = 140; x < 320; x++) {
    const g = clamp(1 - Math.abs(x - CO.cx) / 95, 0, 1);
    for (let y = 196; y < 199; y++) p.set(x, y, pick(['#4a3030', '#6a4638', '#946a4c', '#b88a5e'], 0.2 + 0.6 * g + (hash(x, y, 20) - 0.5) * 0.3, x, y));
    p.set(x, 194, '#3a221c');                                        // the fence round the monument
    if (x % 4 === 0) { p.set(x, 195, '#2a1814'); p.set(x, 196, '#2a1814'); }
    for (let y = 199; y < 207; y++) {                                // asphalt with the floodlight's warmth on it
      const gy = g * (1 - (y - 199) / 11);
      p.set(x, y, pick(['#16121a', '#1e1822', '#2a2028', '#3c2a2c', '#56382e', '#704832'], 0.3 + gy * 0.75 + (hash(x, y, 21) - 0.5) * 0.14, x, y));
    }
    p.set(x, 207, x % 7 === 0 ? '#6a5652' : '#8e766a'); p.set(x, 208, '#3e3036');   // the lane's last step
  }
  for (const X of POSTS) {
    for (let y = 181; y < 199; y++) { p.set(X, y, '#1a1216'); if (y > 194) p.set(X - 1, y, '#1a1216'); }
    p.fill(X - 1, 177, 3, 1, '#2a1c1c'); p.fill(X - 1, 178, 3, 3, '#ffd890'); p.set(X, 176, '#2a1c1c'); p.fill(X - 1, 181, 3, 1, '#2a1c1c');
  }
  return trim(p);
}
const CAR_ROWS = ['...rrrrr....', '..rwwrwwr...', 'tbbbbbbbbbbL', 'dbbbbbbbbbbb', '.kk.....kk..'];
const SCOOT_ROWS = ['..h...', '..cc..', '.ccc..', 'tbbbbL', '.k..k.'];
const CARS = [
  { t0: 4, dur: 6.5, dir: 1, b: '#8a2424', r: '#b43a34' }, { t0: 19, dur: 5.5, dir: -1, b: '#c8c4bc', r: '#ece8e0' },
  { t0: 33, dur: 3.8, dir: -1, scoot: true, b: '#d8c890' }, { t0: 52, dur: 7, dir: 1, b: '#2a3a5a', r: '#3a4e74' },
  { t0: 77, dur: 5, dir: -1, b: '#1e1e24', r: '#34343c' }, { t0: 96, dur: 4, dir: 1, scoot: true, b: '#8ed0b4' },
  { t0: 121, dur: 6, dir: -1, b: '#c8b040', r: '#e0c860' }, { t0: 139, dur: 6.5, dir: 1, b: '#c8c4bc', r: '#ece8e0' },
  { t0: 163, dur: 3.6, dir: -1, scoot: true, b: '#c84030' }, { t0: 178, dur: 5.5, dir: 1, b: '#5a1a2a', r: '#7a2a3a' },
  { t0: 201, dur: 6, dir: -1, b: '#3a4a3a', r: '#4e624e' }, { t0: 223, dur: 4, dir: 1, scoot: true, b: '#d8c890' },
];
function renderVehicle(v) {
  const rows = v.scoot ? SCOOT_ROWS : CAR_ROWS;
  const pal = { r: v.r, b: v.b, d: '#141018', w: '#2c3446', k: '#0a080c', t: '#ff3a2a', L: '#fff4d0', h: '#e8e0d0', c: '#3a2a3a' };
  const right = sprite(rows, pal), [left, lx] = makeCanvas(right.width, right.height);
  lx.translate(right.width, 0); lx.scale(-1, 1); lx.drawImage(right, 0, 0);
  return { right, left, w: right.width };
}
function drawTraffic(t) {
  CARS.forEach((v, i) => {
    const s = (t - v.t0) / v.dur;
    if (s < 0 || s >= 1) return;
    const spr = VEH[i], x = Math.round(v.dir > 0 ? lerp(146, 314, s) : lerp(314, 146, s)) - (spr.w >> 1), y = 201;
    ctx.drawImage(v.dir > 0 ? spr.right : spr.left, x, y);
    const fx = v.dir > 0 ? x + spr.w - 1 : x, bx = v.dir > 0 ? x : x + spr.w - 1;
    halo(HL.head, fx + v.dir * 2, y + 3, 0.9); halo(HL.tail, bx, y + 2, 0.8);
  });
}
const TOURISTS = [{ k: 2, ph: 0.1, dir: 1, c: '#2a1a1c' }, { k: 3, ph: 0.45, dir: -1, c: '#1c1420' }, { k: 2, ph: 0.72, dir: -1, c: '#301c1a' },
  { k: 3, ph: 0.9, dir: 1, c: '#1e1622' }, { k: 4, ph: 0.3, dir: 1, c: '#2a1a1c' }, { k: 2, ph: 0.58, dir: 1, c: '#241418' }];
function drawTourists(t) {
  TOURISTS.forEach((q, i) => {
    const u = frac(q.k * t / LOOP + q.ph), x = Math.round(q.dir > 0 ? lerp(150, 312, u) : lerp(312, 150, u));
    if (x < 158 || x > 300) return;
    const st = Math.floor(t * 3 + i) & 1;
    ctx.fillStyle = q.c; ctx.fillRect(x, 193, 1, 3); ctx.fillRect(x + (st ? 1 : 0), 196, 1, 1); ctx.fillRect(x - (st ? 0 : 1), 196, 1, 1);
    ctx.fillStyle = '#6a4636'; ctx.fillRect(x, 192, 1, 1);
  });
}

/* ================= Houses: mottled plaster, stone-framed windows, green shutters ================= */
const SHUT = ['#11261c', '#1c3a2a', '#2a5238', '#3e6e48', '#56885a'];
const plasterV = (x, y, seed) => (vnoise(x / 7, y / 7, seed) - 0.5) * 0.24 + (vnoise(x / 2.3, y / 2.3, seed + 1) - 0.5) * 0.1;
/** a window at (x, y) with a stone surround, sill and shutters; style: open | closed | ajar. Returns its light record. */
function paintWindow(p, x, y, w, h, style, L, extra = {}) {
  const fr = ['#4a3a34', '#76604e', '#a48a6c', '#cab08a'], half = w >> 1;
  for (let j = -1; j <= h; j++) for (let i = -1; i <= w; i++) {
    if (i < 0 || j < 0 || i >= w || j >= h) p.set(x + i, y + j, pick(fr, L * 0.85 + (j < 0 ? 0.12 : 0), x + i, y + j));
    else p.set(x + i, y + j, i === half || j === (h >> 1) ? '#231a22' : (i + j) % 7 === 0 ? '#1c1520' : '#130e16');
  }
  for (let i = -2; i <= w + 1; i++) { p.set(x + i, y + h + 1, pick(fr, L + 0.15, x + i, y + h + 1)); p.set(x + i, y + h + 2, '#1e141a'); }
  const slats = (x0, ww) => { for (let j = 0; j < h; j++) for (let i = 0; i < ww; i++)
    p.set(x0 + i, y + j, j % 2 === 0 ? pick(SHUT, L * 0.8 + (i === 0 ? 0.18 : 0) + 0.1, x0 + i, y + j) : SHUT[0]); };
  if (style === 'closed') slats(x, w);
  else {
    if (style === 'ajar') slats(x, half); else slats(x - 1 - half, half);
    slats(x + w + 1, half);
  }
  return { x, y, w, h, style, ...extra };
}
/** per-frame light of a window record: warm glass with a curtain, or warm slits through closed slats */
function drawWindowLight(wn, t) {
  if (wn.kind === 'tv') {
    const s = step(t, 960), I = 0.4 + 0.6 * hash(wn.id, s, 13);
    ctx.globalAlpha = 0.55 + 0.35 * I; rect(ctx, wn.x, wn.y, wn.w, wn.h, ['#3a5cc8', '#6a8ae8', '#9ab8ff', '#4a6ad8'][Math.floor(hash(wn.id, s, 14) * 4)]);
    ctx.globalAlpha = 1; rect(ctx, wn.x + (wn.w >> 1), wn.y, 1, wn.h, '#141a36');
    halo(HL.tv, wn.x + wn.w / 2, wn.y + wn.h / 2, I);
    return;
  }
  if (hash(wn.id, Math.floor(frac(t / LOOP + wn.off) * wn.n), 4) >= wn.p) return;
  const half = wn.w >> 1;
  if (wn.style === 'closed') {
    ctx.fillStyle = '#ffc070'; ctx.globalAlpha = 0.75;
    for (let j = 1; j < wn.h; j += 2) ctx.fillRect(wn.x, wn.y + j, wn.w, 1);
    ctx.globalAlpha = 1;
    halo(HL.winS, wn.x + wn.w / 2, wn.y + wn.h / 2, 0.7);
    return;
  }
  const x0 = wn.style === 'ajar' ? wn.x + half : wn.x, ww = wn.style === 'ajar' ? wn.w - half : wn.w;
  rect(ctx, x0, wn.y, ww, wn.h, '#f4a852'); rect(ctx, x0, wn.y, ww, Math.max(2, wn.h >> 2), '#ffd08a');
  rect(ctx, x0 + ww - 2, wn.y, 2, wn.h, '#ffe6b8');                                    // curtain
  rect(ctx, wn.x + half, wn.y, 1, wn.h, '#6a4230'); rect(ctx, x0, wn.y + (wn.h >> 1), ww, 1, '#8a5436');
  halo(HL.win, wn.x + wn.w / 2, wn.y + wn.h / 2, 0.85);
}
/** roof tiles (coppi) and a projecting eave from x0 to x1 at y */
function paintEave(p, x0, x1, y) {
  for (let x = x0; x <= x1; x++) {
    for (let j = 1; j <= 4; j++) p.set(x, y - j, (x + (j & 1) * 2) % 4 === 0 ? '#3a1a16' : j === 4 ? '#8a4a34' : '#6a3226');
    p.set(x, y, '#9a7a62'); p.set(x, y + 1, '#5a4440'); p.set(x, y + 2, '#1c1218');
  }
}
function facadeFill(p, x0, x1, yTop, yBot, ramp, seed, light) {
  for (let x = x0; x < x1; x++) {
    const yt = typeof yTop === 'function' ? yTop(x) : yTop, yb = typeof yBot === 'function' ? yBot(x) : yBot;
    for (let y = Math.round(yt); y < Math.round(yb); y++) p.set(x, y, pick(ramp, light(x, y) + plasterV(x, y, seed), x, y));
  }
}

/* ================= Down the lane: a wine bar's house (left) and the house under restoration (right) ================= */
const OCHRE = ['#241622', '#36202a', '#4e2e2e', '#6a4032', '#8a5638', '#a86e40', '#c48a4c', '#daa45a'];
const ROSA = ['#261624', '#3a1e2a', '#522a32', '#6e3a3a', '#8c4c44', '#a8624e', '#c07a5a', '#d49468'];
const lampLight = (x, y, l, rx, ry, k) => k * Math.max(0, 1 - Math.hypot((x - l.x) / rx, (y - l.y) / ry));
function renderL2() {
  const p = pix(W, H), wins = [], r = rng(121);
  const light = (x, y) => 0.26 + lampLight(x, y, LANT_M, 46, 40, 0.5) + 0.2 * smooth(186, 226, y) - 0.08 * smooth(140, 96, y);
  facadeFill(p, L2.l, L2.r, L2.top, L2.base, OCHRE, 120, light);
  facadeFill(p, L2.r, L2.side, x => recede(L2.top, L2.r, x), x => recede(L2.base, L2.r, x), OCHRE, 122, (x, y) => light(x, y) - 0.08);
  paintEave(p, L2.l, L2.r, L2.top);
  for (let x = L2.r; x < L2.side; x++) { const y = Math.round(recede(L2.top, L2.r, x)); p.set(x, y, '#8a6a58'); p.set(x, y + 1, '#1c1218'); for (let j = 1; j <= 3; j++) p.set(x, y - j, '#5a2c22'); }
  for (const y of [128, 158, 190]) for (let x = L2.l; x < L2.side; x++) { const yy = x < L2.r ? y : Math.round(recede(y, L2.r, x)); p.set(x, yy, pick(OCHRE, light(x, yy) + 0.25, x, yy)); }
  const styles = [['open', 'closed'], ['ajar', 'open'], ['closed', 'open']];
  [104, 136, 166].forEach((y, fi) => [121, 138].forEach((x, ci) => wins.push(paintWindow(p, x, y, 7, 12, styles[fi][ci], light(x, y),
    { id: 40 + fi * 2 + ci, n: 2 + Math.floor(r() * 5), p: 0.55, off: r() }))));
  for (const y of [104, 136, 166]) for (let x = L2.r + 2; x < L2.side - 1; x += 3) {           // slivers of side windows
    const y0 = Math.round(recede(y, L2.r, x)), y1 = Math.round(recede(y + 12, L2.r, x));
    for (let yy = y0; yy < y1; yy++) { p.set(x, yy, '#140e16'); p.set(x + 1, yy, yy % 2 ? '#1c3a2a' : '#2a5238'); }
  }
  // ground floor: the wine bar (arched opening, warm inside), a lit sign, a green door
  for (let y = 204; y < L2.base; y++) for (let x = 124; x < 137; x++) {
    const arch = y < 210 && Math.hypot(x - 130, y - 210) > 6.6;
    if (arch) continue;
    let col = pick(['#b05a26', '#e08a3a', '#ffb45a', '#ffd488'], 1 - (y - 204) / 26 * 0.7, x, y);
    if (y === 214 && x > 125 && x < 136) col = '#6a3418';
    if (y === 213 && x > 125 && x < 136 && x % 2) col = ['#3a8a4a', '#c83040', '#e8d070', '#f0f0f0'][x % 4];
    if (y > 218 && x > 126 && x < 131) col = '#3a1e14';                                    // someone at the counter
    p.set(x, y, col);
  }
  p.fill(129, 216, 2, 3, '#3a1e14');
  for (let x = 124; x < 142; x++) for (let y = 196; y < 202; y++) p.set(x, y, y === 196 ? '#3a6a4a' : '#1a3426');
  textP(p, 'VINI', 126, 197, '#f4e4c0');
  for (let y = 206; y < L2.base; y++) for (let x = 141; x < 150; x++) p.set(x, y, y < 209 ? '#e0a050' : x === 145 ? '#0e1a14' : (x & 1 ? '#1c3226' : '#24402e'));
  paintPot(p, 151, 230, 'bush');
  return { ...trim(p), wins };
}
/** a terracotta pot with a small lemon tree or a round bush, standing on the cobbles at (x, base) */
function paintPot(p, x, base, kind) {
  for (let y = base - 6; y < base; y++) for (let i = -3; i <= 3; i++) {
    if (Math.abs(i) > 3 - (y > base - 3 ? 1 : 0)) continue;
    p.set(x + i, y, y === base - 6 ? '#c86a3a' : i < 0 ? '#a0502c' : '#6a3020');
  }
  const cy = kind === 'lemon' ? base - 16 : base - 10, rr = kind === 'lemon' ? 5 : 4;
  if (kind === 'lemon') for (let y = cy; y < base - 6; y++) p.set(x, y, '#3a2418');
  for (let y = cy - rr; y <= cy + rr; y++) for (let i = -rr - 1; i <= rr + 1; i++) {
    const v = 1 - (i / (rr + 1)) ** 2 - ((y - cy) / rr) ** 2 + (hash(x + i, y, 190) - 0.5) * 0.5;
    if (v <= 0) continue;
    p.set(x + i, y, pick(LEAF, 0.3 + (i < 0 ? 0.25 : 0) - (y - cy) / rr * 0.2 + hash(x + i, y, 191) * 0.2, x + i, y));
  }
  if (kind === 'lemon') for (const [i, j] of [[-3, -1], [2, -3], [1, 2], [-1, 3], [3, 1]]) p.set(x + i, cy + j, '#f0d040');
}
function renderR2(board) {
  const p = pix(W, H), wins = [], r = rng(131);
  const light = (x, y) => 0.24 + 0.22 * smooth(186, 222, y) + lampLight(x, y, LANT_R, 60, 50, 0.3) - 0.06 * smooth(150, 112, y)
    + (board ? 0.3 * Math.max(0, 1 - Math.hypot((x - BB.x - 24) / 40, (y - 118) / 30)) : 0);
  facadeFill(p, R2.l, R2.r, R2.top, R2.base, ROSA, 130, light);
  facadeFill(p, R2.side, R2.l, x => recede(R2.top, R2.l, x), x => recede(R2.base, R2.l, x), ROSA, 132, (x, y) => light(x, y) - 0.1);
  paintEave(p, R2.l, R2.r, R2.top);
  for (let x = R2.side; x < R2.l; x++) { const y = Math.round(recede(R2.top, R2.l, x)); p.set(x, y, '#8a6a58'); p.set(x, y + 1, '#1c1218'); for (let j = 1; j <= 3; j++) p.set(x, y - j, '#5a2c22'); }
  for (const y of [140, 166, 192]) for (let x = R2.side; x < R2.r; x++) { const yy = x >= R2.l ? y : Math.round(recede(y, R2.l, x)); p.set(x, yy, pick(ROSA, light(x, yy) + 0.25, x, yy)); }
  const styles = [['open', 'closed', 'ajar'], ['closed', 'open', 'open'], ['open', 'ajar', 'closed']];
  [120, 147, 172].forEach((y, fi) => [312, 329, 346].forEach((x, ci) => {
    const w = paintWindow(p, x, y, 8, 13, styles[fi][ci], light(x, y), { id: 60 + fi * 3 + ci, n: 2 + Math.floor(r() * 5), p: 0.5, off: r() });
    if (!board || fi === 2) wins.push(w);
  }));
  // ground floor: the bar's glass door and window, warm inside, under a small awning
  for (let y = 198; y < R2.base; y++) for (let x = 312; x < 352; x++) {
    if (x > 325 && x < 330) continue;
    let col = pick(['#a85424', '#e08638', '#ffb058', '#ffd690'], 1 - (y - 198) / 28 * 0.7, x, y);
    if (x === 312 || x === 325 || x === 330 || x === 351 || y === 198) col = '#3a2420';
    if (x > 334 && x < 348 && y > 210 && y < 222 && hash(x >> 1, y >> 2, 5) < 0.45) col = '#4a2618';   // patrons inside
    if (y === 214 && x > 330) col = '#6a3a22';
    p.set(x, y, col);
  }
  for (let x = 310; x < 354; x++) { p.set(x, 196, (x >> 1) % 2 ? '#e8dcc0' : '#2a5a3e'); p.set(x, 197, (x >> 1) % 2 ? '#a89a80' : '#1c3e2a'); }
  p.fill(316, 188, 17, 7, '#1a1014');                                                     // the neon's backing plate
  paintPot(p, 303, 222, 'lemon');
  if (board) paintScaffold(p);
  return { ...trim(p), wins };
}
/** R2's scaffold: tubes, planks, braces, a green debris net, the hoarding's frame and its two lamp arms */
function paintScaffold(p) {
  const tube = y => y < 150 ? '#8a8a96' : '#5a5a68';
  for (const x of [305, 321, 337, 353]) for (let y = 108; y < R2.base; y++) { p.set(x, y, tube(y)); if (y % 12 === 0) p.set(x, y, '#2a2a34'); }
  for (const y of [110, 140, 170]) {
    for (let x = 303; x < 364; x++) p.set(x, y, tube(y));
    if (y > 110) for (let x = 304; x < 364; x++) { p.set(x, y + 1, x % 9 === 0 ? '#3a2616' : '#6a4a2c'); p.set(x, y + 2, '#2a1c14'); }
  }
  for (const [x0, y0, x1, y1] of [[305, 170, 321, 142], [337, 170, 353, 142], [321, 196, 337, 172]]) {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let s = 0; s <= n; s++) p.set(lerp(x0, x1, s / n), lerp(y0, y1, s / n), '#6a6a78');
  }
  for (let y = 143; y < 170; y++) for (let x = 304; x < 364; x++) {                      // debris net over the second floor
    if ((x + y) & 1) continue;
    p.set(x, y, y < 150 ? '#2e5a44' : (hash(x, y, 9) < 0.2 ? '#1a3428' : '#22463a'));
  }
  for (let x = BB.x - 1; x <= BB.x + BB.w; x++) { p.set(x, BB.y - 1, '#1a1216'); p.set(x, BB.y + BB.h, '#2a2024'); p.set(x, BB.y + BB.h + 1, '#141014'); }
  for (let y = BB.y - 1; y <= BB.y + BB.h; y++) { p.set(BB.x - 1, y, '#241a1e'); p.set(BB.x + BB.w, y, '#141014'); }
  for (const lx of BB_LAMPS) { p.fill(BB.x + lx, 110, 1, 4, '#3a3a46'); p.fill(BB.x + lx - 1, 114, 3, 1, '#2a2a34'); }
}

/* ================= The billboard: a made-up gelateria, LUNA (a cone of three scoops under two floodlamps) ================= */
const BB_BLUE = ['#0a1034', '#111a4a', '#1a2862', '#26387a', '#344a92', '#4a62a8'];
/** posterised ramp: flat bands with a narrow dither seam (a printed poster, not a gradient) */
function pickFlat(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * 0.3) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
function disc(p, cx, cy, r, colFn) {
  for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++) for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
    const dx = (x - cx) / r, dy = (y - cy) / r;
    if (dx * dx + dy * dy <= 1) p.set(x, y, typeof colFn === 'function' ? colFn(dx, dy, x, y) : colFn);
  }
}
function renderAd() {
  const p = pix(BB.w, BB.h), m = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) {
    const v = 0.12 + BB_LAMPS.reduce((s, lx) => s + 0.55 * Math.max(0, 1 - Math.hypot((i - lx) / 17, j / 24)), 0);
    p.set(i, j, pickFlat(BB_BLUE, v, i, j));
  }
  const scoop = (cx, cy, r, pal) => disc(p, cx, cy, r, (dx, dy, x, y) => { m.set(x, y, '#ffffff'); return pal[dx + dy < -0.7 ? 2 : dx + dy > 0.8 ? 0 : 1]; });
  for (let j = 11; j < 20; j++) {                                          // waffle cone
    const hw = (19.5 - j) * 0.56;
    for (let i = Math.round(8 - hw); i <= Math.round(8 + hw - 1); i++) {
      p.set(i, j, j === 11 ? '#a86a2c' : (i + j) % 3 === 0 || (i - j + 30) % 3 === 0 ? '#9a5e24' : i < 7 ? '#f0b862' : '#d89a4a');
      m.set(i, j, '#ffffff');
    }
  }
  scoop(5.5, 9, 3, ['#b8386a', '#ff6a9a', '#ffb0cc']);                     // strawberry
  scoop(10.5, 9.2, 3, ['#c8b48a', '#fff0cc', '#ffffff']);                  // fior di latte
  scoop(8, 5, 3.1, ['#4e7a34', '#8cc060', '#c8ea98']);                     // pistachio on top
  p.set(7, 3, '#ffffff'); p.set(4, 8, '#ffe0ec');
  const txt = (s, x, y, sc, col, sh) => { if (sh) textP(p, s, x + sc, y + 1, sh, sc); textP(p, s, x, y, col, sc); textP(m, s, x, y, '#ffffff', sc); };
  txt('GELATO', 18, 2, 1, '#fff0d4', null);
  txt('LUNA', 16, 9, 2, '#ff82d4', '#2a0a3a');
  return { c: p.done(), mask: m.done() };
}
const AD_STARS = [[45, 2, 90], [46, 6, 110], [14, 1, 130], [13, 5, 70], [1, 1, 150]];
function drawBillboard(t, pitch) {
  const hum = 0.95 + 0.05 * wave(t, 1500, 0.2), off = inEvent(t, BB_FLICK) && hash(77, Math.floor(t * 18), 2) < 0.5;
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + 6, (off ? 0.7 : 1) * hum);
  if (!BB_PITCH.covers(t, pitch)) {
    ctx.drawImage(AD.c, BB.x, BB.y);
    ctx.fillStyle = '#fff6d8';
    for (const [x, y, k] of AD_STARS) { ctx.globalAlpha = 0.35 + 0.65 * Math.max(0, wave(t, k, x * 0.01)); ctx.fillRect(BB.x + x, BB.y + y, 1, 1); }
    ctx.globalAlpha = 1;
    const dr = frac(t / 20);                                               // a strawberry drip runs down the cone
    if (dr < 0.3) { const y = Math.round(11 + dr / 0.3 * 5); rect(ctx, BB.x + 4 + (y > 13 ? 1 : 0), BB.y + y, 1, 1, '#ff6a9a'); }
    const f = frac(t / 16);                                                // a gloss sheen rolls over the poster
    if (f < 0.12) {
      const bx = lerp(-10, BB.w + 10, f / 0.12);
      for (let j = 0; j < BB.h; j++) {
        const sx = Math.round(bx - j * 0.6), a = Math.max(0, sx), b = Math.min(BB.w, sx + 3);
        if (b <= a) continue;
        ctx.globalAlpha = 0.14; rect(ctx, BB.x + a, BB.y + j, b - a, 1, '#ffffff');
        ctx.globalAlpha = 0.55; ctx.drawImage(AD.mask, a, j, b - a, 1, BB.x + a, BB.y + j, b - a, 1);
      }
      ctx.globalAlpha = 1;
    }
    if (off) { ctx.globalAlpha = 0.35; rect(ctx, BB.x + 26, BB.y, 22, BB.h, '#060818'); ctx.globalAlpha = 1; }
  }
  BB_PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {
    if (off && k === 1) return;
    rect(ctx, BB.x + lx - 1, 115, 3, 1, '#fff4dc');
    halo(HL.bbLamp, BB.x + lx, 116, hum);
  });
}
const BB_FLICK = [[88.4, 89.5], [197.2, 198.6]];                           // the right lamp stutters

/* ================= The lane: sampietrini in perspective, a travertine gutter down the middle ================= */
const BASALT = ['#0c0c16', '#13131e', '#1a1926', '#23202e', '#2d2834', '#3a3038', '#4c3c3a', '#624c40', '#7c5e48', '#9a7450', '#ba8c5c'];
const MORTAR = ['#050409', '#0a080e', '#141014', '#221a1a', '#30241e'];
const GUTTER = ['#120e16', '#1c1820', '#28222a', '#352c30', '#443836', '#56463e', '#6a5646'];
function groundLight(x, y) {
  return lampLight(x, y, { x: LANT_L.x + 4, y: 252 }, 74, 24, 0.62) + lampLight(x, y, { x: LANT_R.x - 6, y: 248 }, 66, 22, 0.58)
    + lampLight(x, y, { x: LANT_M.x + 6, y: 227 }, 40, 11, 0.5) + lampLight(x, y, { x: 46, y: 258 }, 96, 22, 0.42)
    + lampLight(x, y, { x: 332, y: 231 }, 40, 9, 0.4) + lampLight(x, y, { x: CO.cx, y: 210 }, 130, 20, 0.5)
    + lampLight(x, y, { x: 240, y: 262 }, 220, 50, 0.14);
}
const K_COB = 12;                                       // cobbles per unit of 1/(y − GY): their size grows towards us
function renderGround() {
  const p = pix(W, H), r = rng(151);
  for (let y = 209; y < H; y++) {
    const d = y - GY, rq = K_COB * Math.log(d), row = Math.floor(rq), fv = rq - row, sz = d / K_COB;
    for (let x = 0; x < W; x++) {
      const uq = (x - VP.x) / d * K_COB + (row & 1) * 0.5 + hash(row, 0, 33) * 0.3, col = Math.floor(uq), fu = uq - col;
      // the light at the stone's centre, so each stone keeps one flat colour (no dither inside a stone)
      const cx = VP.x + (col + 0.5 - (row & 1) * 0.5 - hash(row, 0, 33) * 0.3) * d / K_COB, Lc = 0.26 + groundLight(cx, y) + (hash(col, row, 30) - 0.5) * 0.14;
      const gu = (x + 0.5 - VP.x) / (0.055 * d + 0.3);
      if (Math.abs(gu) < 1) {                                              // the gutter: a shallow channel of worn travertine
        // slabs two cobbles long, each one flat colour about as bright as the cobbles' lit tops, so it reads as paving
        const sq = rq / 2, slab = Math.floor(sq), Lg = 0.26 + groundLight(VP.x, y);
        if (sq - slab < 6 / d) { p.set(x, y, MORTAR[clamp(Math.round(Lg * 4), 0, 4)]); continue; }
        const au = Math.abs(gu), v = Lg * 0.9 + (au > 0.7 ? 0.09 : au < 0.3 ? -0.05 : 0.01) + (hash(slab, gu > 0 ? 1 : 0, 41) - 0.5) * 0.16;   // lit lips, a damp middle
        p.set(x, y, GUTTER[clamp(Math.round(v * (GUTTER.length - 1)), 0, GUTTER.length - 1)]);
        continue;
      }
      if (sz < 2.4) { p.set(x, y, pick(BASALT, Lc + (hash(x, y, 31) < 0.3 ? -0.1 : 0), x, y)); continue; }
      const mortar = fv < 0.13 + 0.5 / sz || fu < 0.1 + 0.5 / sz;
      if (mortar) { p.set(x, y, MORTAR[clamp(Math.round(Lc * 5), 0, 4)]); continue; }
      let k = Math.round(Lc * (BASALT.length - 1));
      if (fv < 0.42 && fu < 0.7) k += 1;                                    // the domed top catches the light
      else if (fv > 0.82 || fu > 0.9) k -= 1;
      p.set(x, y, BASALT[clamp(k, 0, BASALT.length - 1)]);
    }
  }
  for (let k = 0; k < 110; k++) {                                          // fallen bougainvillea bracts
    let x, y;
    if (k < 55) { x = 330 + r() * 150; y = 238 + r() * 30; } else if (k < 90) { x = 150 + r() * 200; y = 216 + r() * 52; } else { x = r() * 150; y = 252 + r() * 18; }
    const c = r() < 0.35 ? '#ff5eb0' : r() < 0.6 ? '#d0308a' : '#8a1a5c';
    p.set(x, y, c); if (y > 232 && r() < 0.6) { p.set(x + 1, y, c); if (y > 248) p.set(x, y - 1, '#e03a94'); }
  }
  for (let x = 358; x < 368; x++) for (let y = 247; y < 250; y++) p.set(x, y, (x & 1) && y === 248 ? '#020204' : '#2a2a30');   // the drain grate
  for (let y = 249; y < 254; y++) for (let x = 346; x < 368; x++) {                              // the nasone's puddle
    const e = ((x - 356) / 10) ** 2 + ((y - 251) / 2.4) ** 2;
    if (e < 1) p.set(x, y, e > 0.7 ? '#1c1a26' : pick(['#101420', '#1a2234', '#2a3448'], 0.5 - (y - 251) * 0.2, x, y));
  }
  return p.done();
}

/* ================= The trattoria's house (left foreground) ================= */
const L1_FLOORS = [26, 64, 102, 140], L1_COLS = [9, 40, 71];
const L1_STYLE = [['closed', 'open', 'ajar'], ['open', 'tv', 'closed'], ['ajar', 'closed', 'watch'], ['open', 'open', 'closed']];
const PEEL = [[28, 96, 8, 5], [84, 54, 6, 4], [14, 166, 7, 4], [58, 128, 5, 3]];
const l1Light = (x, y) => 0.2 + lampLight(x, y, LANT_L, 74, 66, 0.46) + 0.32 * smooth(150, 192, y) + 0.1 * smooth(90, 160, y) - 0.1 * smooth(60, 10, y);
function renderL1() {
  const p = pix(W, H), wins = [], r = rng(141);
  facadeFill(p, 0, L1.r, L1.top, L1.base, OCHRE, 140, l1Light);
  facadeFill(p, L1.r, L1.side, x => recede(L1.top, L1.r, x), x => recede(L1.base, L1.r, x), OCHRE, 142,
    (x, y) => l1Light(x, y) - 0.1 + lampLight(x, y, LANT_L, 16, 40, 0.3));
  for (const [cx, cy, rx, ry] of PEEL) for (let y = cy - ry - 2; y <= cy + ry + 2; y++) for (let x = cx - rx - 2; x <= cx + rx + 2; x++) {
    const v = 1 - ((x - cx) / rx) ** 2 - ((y - cy) / ry) ** 2 + (vnoise(x / 2, y / 2, 143) - 0.5) * 0.9;   // peeled plaster, bricks beneath
    if (v <= 0) continue;
    const L = l1Light(x, y), mortar = y % 3 === 0 || (x + (Math.floor(y / 3) & 1) * 3) % 6 === 0;
    p.set(x, y, v < 0.25 ? pick(OCHRE, L + 0.28, x, y) : mortar ? pick(['#2a1a1e', '#4a3030', '#6a4a40'], L, x, y) : pick(BRICK, L + 0.1, x, y));
  }
  paintEave(p, 0, L1.r, L1.top + 4);
  for (let x = 0; x < L1.r; x += 6) { p.fill(x + 1, L1.top + 7, 2, 2, '#7a604e'); p.set(x + 1, L1.top + 9, '#1e1418'); }   // eave brackets
  for (let x = L1.r; x < L1.side; x++) { const y = Math.round(recede(L1.top + 4, L1.r, x)); for (let j = 0; j < 3; j++) p.set(x, y + j, j ? '#1c1218' : '#8a6a58'); for (let j = 1; j <= 4; j++) p.set(x, y - j, '#5a2c22'); }
  for (const y of [56, 94, 132]) for (let x = 0; x < L1.side; x++) {                          // stone string courses
    const yy = x < L1.r ? y : Math.round(recede(y, L1.r, x));
    p.set(x, yy, pick(['#5a4a44', '#8a7460', '#b89c7c', '#d8bc94'], l1Light(x, yy) + 0.2, x, yy)); p.set(x, yy + 1, '#221820');
  }
  L1_FLOORS.forEach((y, fi) => L1_COLS.forEach((x, ci) => {
    const st = L1_STYLE[fi][ci], kind = st === 'tv' ? 'tv' : st === 'watch' ? 'watch' : 'win';
    wins.push(paintWindow(p, x, y, 14, 20, kind === 'win' ? st : 'open', l1Light(x, y), { id: 10 + fi * 3 + ci, kind, n: 2 + Math.floor(r() * 6), p: 0.6, off: r() }));
  }));
  for (const y of L1_FLOORS) for (let x = L1.r + 3; x < L1.side - 2; x += 5) {                // side windows, seen edge-on
    const y0 = Math.round(recede(y, L1.r, x)), y1 = Math.round(recede(y + 20, L1.r, x));
    for (let yy = y0; yy < y1; yy++) { p.set(x, yy, '#120c14'); p.set(x + 1, yy, '#130e16'); p.set(x + 2, yy, yy % 2 ? SHUT[1] : SHUT[3]); }
  }
  for (let x = 34; x < 64; x++) {                                                              // first-floor balcony with geraniums
    p.set(x, 162, '#8a7460'); p.set(x, 163, '#3a2a2a');
    for (let y = 155; y < 162; y++) if (x % 3 === 0 || y === 155) p.set(x, y, '#1a1216');
  }
  for (let k = 0; k < 7; k++) { const x = 36 + k * 4; p.fill(x, 152, 3, 2, '#7a3a24'); p.set(x, 150, '#e03a3a'); p.set(x + 1, 151, '#ff5a4a'); p.set(x + 2, 150, '#2a5a2a'); p.set(x + 1, 149, '#c02a30'); }
  // the trattoria: sign band, rusticated stone, two arched windows full of warm light, the open door
  for (let x = 0; x < L1.r; x++) for (let y = 174; y < 184; y++) p.set(x, y, y === 174 || y === 183 ? '#c8a878' : y === 175 || y === 182 ? '#6a1c20' : '#4a1418');
  textP(p, 'TRATTORIA', 30, 177, '#f6e6c0');
  for (let y = 184; y < L1.base; y++) for (let x = 0; x < L1.r; x++) {
    const joint = (y - 184) % 9 === 0 || (x + (Math.floor((y - 184) / 9) & 1) * 11) % 22 === 0;
    p.set(x, y, joint ? pick(['#2a1c20', '#4a3630', '#6a5040'], l1Light(x, y), x, y) : pick(['#4a3a38', '#6a544a', '#8e725c', '#b0906e', '#caa880'], l1Light(x, y) + 0.1 + (hash(x >> 2, y >> 2, 5) - 0.5) * 0.12, x, y));
  }
  for (const x0 of [6, 38]) {
    const cx = x0 + 12.5;
    for (let y = 189; y < 239; y++) for (let x = x0 - 1; x < x0 + 27; x++) {
      const inArch = y >= 203 || Math.hypot(x - cx, y - 203) <= 13;
      const frame = !inArch ? false : (y < 203 ? Math.hypot(x - cx, y - 203) > 12 : x < x0 || x > x0 + 25);
      if (!inArch) continue;
      let col;
      if (frame) col = '#caa880';
      else if (y >= 231) col = y === 231 ? '#8a5a34' : '#4a2a1c';                                     // wooden panel
      else {
        col = pick(['#e0863a', '#f8b25e', '#ffd690'], 1 - (y - 190) / 42 * 1.1, x, y);                    // warm room, flat-lit
        if (y === 208) col = '#6a3418';                                                                  // shelf
        if (y === 207 && x % 2 === 0) col = ['#2a5a2a', '#7a1a1a', '#e8d070', '#3a2a5a'][(x >> 1) % 4];   // bottles
        if (y >= 225 && y < 231) col = y === 225 ? '#8a2a24' : '#5a2418';                                // a table inside
        for (const hx of [x0 + 6, x0 + 17]) {                                                            // diners inside
          if (y >= 217 && y < 220 && x >= hx && x < hx + 2) col = '#3a1c14';
          if (y >= 220 && y < 225 && x >= hx - 1 && x < hx + 3) col = '#4a2418';
        }
        if (y === 194 && (x === x0 + 8 || x === x0 + 18)) col = '#fff6d8';                               // pendant lamps
        if (y < 194 && (x === x0 + 8 || x === x0 + 18)) col = '#6a4a30';
      }
      p.set(x, y, col);
    }
  }
  for (let y = 190; y < L1.base; y++) for (let x = 70; x < 91; x++) {                            // the open door, a bead curtain
    const r0 = Math.hypot(x - 80, y - 200);
    if (y < 200 && r0 > 10) continue;
    let col = pick(['#c8682e', '#f09848', '#ffc878'], 0.95 - (y - 190) / 62 * 0.7, x, y);
    if ((y < 200 && r0 > 9) || x === 70 || x === 90) col = '#caa880';
    else if (y > 199 && y < 244 && (x - 72) % 4 === 0) col = (y % 3 === 0) ? '#ffd890' : '#7a2e1c';
    else if (y >= 244) col = '#3a2014';
    p.set(x, y, col);
  }
  return { ...trim(p), wins };
}

/* ================= The garden house (right foreground): terracotta, ivy, a corner shrine, bougainvillea ================= */
const TERRA = ['#221220', '#361a24', '#4e2228', '#6a2c2c', '#883830', '#a44836', '#bc5e3e', '#d0784a'];
const BOUG = ['#2e0826', '#4e0e3a', '#76164e', '#a01e68', '#cc2c84', '#f0469e', '#ff72bc', '#ffa6d6'];
const LEAF = ['#08140e', '#0e2016', '#16301e', '#224228', '#325a34'];
const R1_FLOORS = [22, 60, 98, 136], R1_COLS = [398, 432, 466];
const R1_STYLE = [['open', 'closed', 'ajar'], ['ajar', 'open', 'closed'], ['closed', 'open', 'open'], ['open', 'ajar', 'closed']];
const SHRINE = { x: 386, y: 110 };
const r1Light = (x, y) => 0.18 + lampLight(x, y, LANT_R, 70, 70, 0.5) + 0.12 * smooth(150, 200, y) - 0.08 * smooth(60, 0, y);
const BLOOM = [[410, 172, 24, 22], [396, 188, 14, 12], [430, 158, 16, 13], [446, 180, 13, 12], [418, 146, 11, 8], [456, 194, 9, 5],
  [400, 204, 12, 6], [417, 211, 9, 10], [432, 204, 11, 6], [395, 216, 5, 10], [410, 223, 4, 7], [440, 210, 5, 7], [388, 166, 6, 12]];
const bloomV = (x, y) => { let v = -9; for (const [cx, cy, rx, ry] of BLOOM) v = Math.max(v, 1 - ((x - cx) / rx) ** 2 - ((y - cy) / ry) ** 2); return v; };
const ivyV = (x, y) => (x - 452) / 22 + (vnoise(x / 9, y / 11, 161) - 0.5) * 1.6 - smooth(120, 70, y) * 1.5 + smooth(150, 196, y) * 0.4;
function renderR1() {
  const p = pix(W, H), wins = [], r = rng(171);
  facadeFill(p, R1.l, W, 0, 200, TERRA, 170, r1Light);
  facadeFill(p, R1.side, R1.l, x => recede(0, R1.l, x), x => recede(R1.base, R1.l, x), TERRA, 172, (x, y) => r1Light(x, y) - 0.08 + lampLight(x, y, LANT_R, 14, 40, 0.3));
  for (const y of [52, 90, 128]) for (let x = R1.side; x < W; x++) {
    const yy = x >= R1.l ? y : Math.round(recede(y, R1.l, x));
    p.set(x, yy, pick(['#5a4a44', '#8a7460', '#b89c7c'], r1Light(x, yy) + 0.2, x, yy)); p.set(x, yy + 1, '#1e1418');
  }
  R1_FLOORS.forEach((y, fi) => R1_COLS.forEach((x, ci) => wins.push(paintWindow(p, x, y, 13, 20, R1_STYLE[fi][ci], r1Light(x, y),
    { id: 30 + fi * 3 + ci, n: 2 + Math.floor(r() * 6), p: 0.55, off: r() }))));
  for (const y of R1_FLOORS) for (let x = R1.side + 4; x < R1.l - 3; x += 6) {
    const y0 = Math.round(recede(y, R1.l, x)), y1 = Math.round(recede(y + 20, R1.l, x));
    for (let yy = y0; yy < y1; yy++) { p.set(x, yy, yy % 2 ? SHUT[1] : SHUT[3]); p.set(x + 1, yy, '#120c14'); p.set(x + 2, yy, '#130e16'); }
  }
  // ivy over the right of the house
  for (let y = 40; y < 200; y++) for (let x = 426; x < W; x++) {
    const v = ivyV(x, y);
    if (v <= 0) continue;
    const t = vnoise(x / 1.6, y / 1.6, 162), lit = r1Light(x, y);
    if (t < 0.25 && v < 0.6) continue;
    p.set(x, y, pick(LEAF, 0.25 + t * 0.5 + lit * 0.4 - (hash(x, y, 163) < 0.2 ? 0.2 : 0), x, y));
  }
  // the corner shrine: a tiled hood, a stucco frame, the Madonna in blue under a golden halo, a red votive lamp
  const S = SHRINE;
  for (let x = S.x; x <= S.x + 13; x++) { p.set(x, S.y, x === S.x || x === S.x + 13 ? '#3a1a18' : '#6a2a20'); p.set(x, S.y + 1, '#a45434'); p.set(x, S.y + 2, '#2a1418'); }
  for (let y = S.y + 3; y < S.y + 27; y++) for (let x = S.x + 1; x <= S.x + 12; x++) {
    const inner = x >= S.x + 3 && x <= S.x + 10 && y >= S.y + 5 && y < S.y + 25 && !(y < S.y + 8 && Math.hypot(x - S.x - 6.5, y - S.y - 8) > 3.8);
    if (!inner) { p.set(x, y, pick(['#8a7a68', '#c8b896', '#eee0c0'], 0.6 + (x < S.x + 4 ? 0.2 : 0) - (y > S.y + 24 ? 0.3 : 0), x, y)); continue; }
    let col = '#1a2a6a';
    const cx = S.x + 6.5;
    if (Math.hypot(x - cx, y - S.y - 10) < 2.9) col = '#f0c860';
    if (Math.abs(x - cx) < 1 && y >= S.y + 9 && y <= S.y + 11) col = '#e8c8a0';
    if (y >= S.y + 12 && Math.abs(x - cx) <= (y - S.y - 12) * 0.35 + 1) col = x < cx ? '#6a8ae8' : '#3a5ac8';
    if (y >= S.y + 22) col = '#c8a050';
    p.set(x, y, col);
  }
  for (let x = S.x; x <= S.x + 13; x++) { p.set(x, S.y + 27, '#c8b896'); p.set(x, S.y + 28, '#4a3a34'); }
  for (const [dx, c] of [[2, '#ff4a5a'], [3, '#ffffff'], [10, '#ffffff'], [11, '#ff4a5a']]) { p.set(S.x + dx, S.y + 26, c); p.set(S.x + dx, S.y + 25, '#2a5a2a'); }
  // lantern brackets (the lanterns themselves glow per frame)
  for (const [l, dir] of [[LANT_L, -1], [LANT_R, 1]]) for (let k = 0; k <= 6; k++) p.set(l.x + dir * k, l.y - 6, '#1a1216');
  // garden wall with its coping, a wrought-iron gate
  for (let y = 196; y < R1.base; y++) for (let x = R1.l; x < W; x++) {
    let col;
    if (y < 199) col = y === 196 ? '#d8c4a0' : y === 197 ? '#a08a70' : '#3a2a28';
    else col = pick(['#2a1e24', '#3e2c2e', '#584036', '#765440', '#94704e'], r1Light(x, y) + 0.08 + plasterV(x, y, 175), x, y);
    if (x >= 454 && x < 474 && y >= 203) col = (x - 454) % 3 === 0 ? '#1c2a22' : y < 207 ? '#0e1a14' : pick(LEAF, 0.2 + vnoise(x / 2, y / 2, 176) * 0.4, x, y);
    if ((x === 452 || x === 453 || x === 474 || x === 475) && y >= 200) col = pick(['#5a4a44', '#8a7460', '#b89c7c'], 0.5, x, y);
    p.set(x, y, col);
  }
  for (let x = 454; x < 474; x++) { p.set(x, 203, '#2a3a30'); p.set(x, 208, '#1c2a22'); }
  // bougainvillea: a dark mass of leaves, then clusters of magenta bracts shaded towards the lantern
  for (let y = 136; y < 232; y++) for (let x = 380; x < 466; x++) {
    const v = bloomV(x, y) + (vnoise(x / 5, y / 5, 177) - 0.5) * 0.6;
    if (v > 0.25 && hash(x, y, 178) < 0.9) p.set(x, y, pick(LEAF, 0.2 + vnoise(x / 2, y / 2, 179) * 0.35, x, y));
  }
  const balls = [];
  for (let k = 0; k < 7000 && balls.length < 360; k++) {
    const x = 380 + r() * 86, y = 136 + r() * 96, v = bloomV(x, y);
    if (v < -0.05 + r() * 0.15) continue;
    balls.push({ x, y, r: 1.2 + r() * 1.9, tint: (r() - 0.5) * 0.16, leaf: r() < 0.16 });
  }
  balls.sort((a, b) => a.y - b.y);
  for (const b of balls) {
    const lx = LANT_R.x - b.x, ly = LANT_R.y - b.y, ld = Math.max(1, Math.hypot(lx, ly)), k = clamp(1 - ld / 80, 0, 1);
    const base = 0.3 + k * 0.42 + b.tint;
    disc(p, b.x, b.y, b.r, (dx, dy, x, y) => {
      const nd = (dx * lx + dy * ly) / ld;
      if (b.leaf) return pick(LEAF, 0.3 + nd * 0.3 + k * 0.3, x, y);
      return pick(BOUG, base + nd * 0.25 - dx * dy * 0.1 + (hash(x, y, 180) - 0.5) * 0.14, x, y);
    });
  }
  return { ...trim(p), wins };
}

/* ================= The nasone (cast-iron street fountain) and the parked scooter ================= */
function renderNasone() {
  const p = pix(18, 28), X = 5;                                            // local: body x 5..11, cap from y 0
  for (let y = 0; y < 26; y++) for (let i = 0; i < 7; i++) {
    if (y < 3 && (i === 0 || i === 6 || (y === 0 && (i === 1 || i === 5)))) continue;
    const band = y === 5 || y === 6 || y === 22;
    const lv = (i >= 4 ? 0.72 - (i - 4) * 0.12 : 0.3 + i * 0.06) + (y < 3 ? 0.12 : 0) + (band ? 0.12 : 0);
    p.set(X + i, y, pick(['#10161a', '#1c2428', '#2a3438', '#3e4c4a', '#62706a', '#9aa89a', '#c8d0c0'], lv + 0.12, X + i, y));
  }
  p.set(X + 3, 0, '#56625a'); p.set(X + 3, -1, '#34403e');
  for (const [i, j] of [[-1, 8], [-2, 8], [-3, 9], [-4, 10], [-4, 11], [-5, 12]]) { p.set(X + i, j, '#2a3432'); p.set(X + i, j - 1, '#56625a'); }   // the curved spout
  p.fill(X - 1, 24, 9, 2, '#1c2224'); p.fill(X - 1, 24, 9, 1, '#3a4442');
  return p.done();
}
const MINT = ['#12302a', '#1e4a40', '#2e6e5a', '#4a9a7e', '#72c4a2', '#a4e4c8', '#d6f8e8'];
function renderVespa() {
  const w = 33, h = 23, p = pix(w, h);
  const tyre = (cx, cy) => { disc(p, cx, cy, 3.3, '#08060a'); disc(p, cx, cy, 1.5, (dx) => dx < 0 ? '#9a9aa8' : '#4a4a56'); p.set(cx, cy, '#1a1a22'); };
  tyre(25, 19); tyre(5, 19);
  const L = (nx, ny, base) => base - nx * 0.28 - ny * 0.38;                 // lit from the lantern, up and to the left
  for (let y = 5; y < 19; y++) for (let x = 12; x < 33; x++) {             // the rounded rear cowl
    const dx = (x - 22.5) / 9.8, dy = (y - 12.5) / 6.4;
    if (dx * dx + dy * dy > 1 || y > 17) continue;
    p.set(x, y, pick(MINT, L(dx, dy, 0.55) + (y === 17 ? -0.2 : 0), x, y));
  }
  for (let x = 16; x < 29; x++) p.set(x, 12, pick(MINT, 0.72, x, 12));      // the cowl's crease line catching the light
  for (let x = 8; x < 17; x++) { p.set(x, 16, '#2a2a32'); p.set(x, 17, pick(MINT, 0.35, x, 17)); }   // floorboard
  for (let y = 4; y < 18; y++) {                                            // legshield
    const xl = Math.round(4 - (y > 12 ? (y - 12) * 0.6 : 0)), xr = Math.round(8 + (y > 11 ? (y - 11) * 0.3 : 0));
    for (let x = xl; x <= xr; x++) p.set(x, y, pick(MINT, L((x - xl) / (xr - xl + 1) * 2 - 1, 0, 0.62) + (x === xl ? 0.18 : 0), x, y));
  }
  for (let x = 1; x <= 9; x++) for (let y = 14; y <= 17; y++) {             // front mudguard
    const dx = (x - 5) / 4.5, dy = (y - 17) / 3;
    if (dx * dx + dy * dy <= 1) p.set(x, y, pick(MINT, L(dx, dy, 0.6), x, y));
  }
  for (let y = 1; y < 5; y++) p.set(6, y, '#6a6a78');                        // steering column
  for (let x = 2; x <= 11; x++) p.set(x, 1, x < 4 || x > 9 ? '#1a1a20' : '#c8ccd8');   // handlebar and grips
  disc(p, 5, 2, 1.6, (dx, dy) => dx * dx + dy * dy > 0.5 ? '#9a9eaa' : '#fff6d8');     // headlight
  for (let x = 14; x < 27; x++) for (let y = 3; y < 6; y++) {               // saddle
    const dx = (x - 20) / 6.5, dy = (y - 5) / 2.2;
    if (dx * dx + dy * dy <= 1) p.set(x, y, y === 3 || (y === 4 && Math.abs(dx) > 0.7) ? '#6a4230' : '#2e1c16');
  }
  p.set(32, 10, '#e02a2a'); p.set(32, 11, '#8a1818'); p.set(31, 10, '#c8ccd8');
  for (let k = 0; k < 4; k++) p.set(19 - (k >> 1), 18 + k, '#3a3a44');      // kickstand
  return p.done();
}

/* ================= The trattoria's tables: checked cloths, bistro chairs, diners ================= */
const DINERS = [
  { t: 0, side: -1, shirt: '#e8e0d0', hair: '#2a1a14' }, { t: 0, side: 1, shirt: '#3a4a8a', hair: '#5a3018' },
  { t: 1, side: -1, shirt: '#8a2a3a', hair: '#1a1210' }, { t: 2, side: -1, shirt: '#2a2a30', hair: '#3a2418' },
  { t: 2, side: 1, shirt: '#c8a040', hair: '#1a1210' },
];
const tableFoot = tb => tb.y + 16;
function paintDiner(p, d) {
  const tb = TABLES[d.t], x0 = tb.x + d.side * (tb.w / 2 + 3), s = d.side, y = tb.y;
  for (let j = -9; j < 16; j++) p.set(x0 + s * 3, y + j, j < 3 ? '#3a2418' : '#2a1a12');                 // chair back and legs
  p.fill(Math.min(x0, x0 + s * 3), y + 3, 4, 1, '#5a3822');
  for (let j = -8; j < 1; j++) for (let i = -1; i <= 1; i++) p.set(x0 - s * i, y + j, i === -1 ? pick(['#140e14', d.shirt], 0.5, x0, y + j) : d.shirt);   // torso
  for (let j = -12; j < -8; j++) for (let i = -1; i <= 1; i++)                                           // head, lit by the candle
    p.set(x0 - s * i, y + j, j === -12 || i === -1 ? d.hair : '#e8a070');
  p.set(x0 - s * 2, y - 3, d.shirt); p.set(x0 - s * 3, y - 2, '#e8a070');                               // forearm on the table
}
function renderTables() {
  const p = pix(W, H);
  for (const d of DINERS) paintDiner(p, d);
  TABLES.forEach((tb, ti) => {
    const x0 = tb.x - (tb.w >> 1);
    for (let j = 0; j < 8; j++) for (let i = -1; i <= tb.w; i++) {
      if (j < 3 && (i < 0 || i >= tb.w)) continue;
      const chk = ((i >> 1) + (j >> 1)) & 1, top = j < 3, lit = top ? 0.1 : -0.12 - (j === 7 ? 0.2 : 0);
      p.set(x0 + i, tb.y + j, chk ? pick(['#6a1418', '#a82028', '#d8343c'], 0.6 + lit, x0 + i, tb.y + j) : pick(['#8a8078', '#d0c4b8', '#fff4e8'], 0.62 + lit, x0 + i, tb.y + j));
    }
    for (let j = 8; j < 16; j++) p.set(tb.x, tb.y + j, '#1a1214');
    p.fill(tb.x - 3, tableFoot(tb), 7, 1, '#1a1214');
    for (const s of [-1, 1]) { p.fill(tb.x + s * (tb.w / 2 - 4) - 1, tb.y, 3, 1, '#ffffff'); p.set(tb.x + s * (tb.w / 2 - 6), tb.y - 2, '#8a1a2a'); p.set(tb.x + s * (tb.w / 2 - 6), tb.y - 1, '#d0c8d8'); }
    if (ti === 0) { p.fill(tb.x - 3, tb.y - 5, 1, 5, '#1a3a22'); p.set(tb.x - 3, tb.y - 4, '#5a9a5a'); p.set(tb.x - 3, tb.y - 6, '#1a3a22'); }
    p.fill(tb.x, tb.y - 2, 2, 2, '#c87a3a');                                                                // the candle's jar
  });
  for (let y = 194; y < 253; y++) { p.set(113, y, '#3a2418'); p.set(114, y, y < 200 ? '#8a5a34' : '#5a3a22'); }   // post for the lights
  for (let y = 236; y < 253; y++) for (let x = 117; x < 127; x++) {                                         // chalkboard menu
    const edge = x === 117 || x === 126 || y === 236;
    p.set(x, y, edge ? '#7a5030' : (y > 238 && y < 250 && (x + y * 3) % 5 === 0 && hash(x, y, 3) < 0.7) ? '#d8d4cc' : '#1a1c1e');
  }
  for (const [a, b] of BULB_SEGS) for (let x = a[0]; x <= b[0]; x++) { const u = (x - a[0]) / (b[0] - a[0]); p.set(x, Math.round(lerp(a[1], b[1], u) + 4 * Math.sin(Math.PI * u)), '#1a1216'); }
  return trim(p);
}
const BULB_SEGS = [[[0, 186], [42, 188]], [[42, 188], [86, 186]], [[86, 186], [113, 195]]];
const BULBS = [];
for (const [a, b] of BULB_SEGS) for (let x = a[0] + 3; x < b[0] - 1; x += 6) { const u = (x - a[0]) / (b[0] - a[0]); BULBS.push({ x, y: Math.round(lerp(a[1], b[1], u) + 4 * Math.sin(Math.PI * u)) + 1 }); }

/* ================= Laundry strung across the lane ================= */
const WIRE = { x0: 112, y0: 62, x1: 368, y1: 68, sag: 10 };
const wireY = x => { const u = (x - WIRE.x0) / (WIRE.x1 - WIRE.x0); return lerp(WIRE.y0, WIRE.y1, u) + WIRE.sag * Math.sin(Math.PI * u); };
const CLOTHES = [
  { x: 120, rows: ['wwwwwww', 'wwwwwww', '.wwwww.', '.wwwww.', '.wwwww.', '.wwwww.'], pal: { w: '#d8d4e0' }, k: 29 },
  { x: 130, rows: ['rrrrr', 'wwwww', 'rrrrr', 'wwwww', 'rrrrr', 'wwwww', 'rrrrr'], pal: { r: '#c84a4a', w: '#e8dcd0' }, k: 33 },
  { x: 138, rows: ['bb', 'bb', 'bb', '.b'], pal: { b: '#3a4a7a' }, k: 41 },
  { x: 142, rows: ['bb', 'bb', 'bb', 'b.'], pal: { b: '#3a4a7a' }, k: 43 },
  { x: 334, rows: ['yyyyyyyyyy', 'yyyyyyyyyy', 'yyyyyyyyyy', 'yyyyyyyyyy', 'yyyyyyyyyy', 'yyyyyyyyyy', 'yyyyyyyyyy', 'yyyyyyyyyy', 'yyyyyyyyyy'], pal: { y: '#e8dcb0' }, k: 23 },
  { x: 348, rows: ['ggggggg', 'ggggggg', '.ggggg.', '.ggggg.', '.ggggg.'], pal: { g: '#6aa88a' }, k: 31 },
  { x: 357, rows: ['pppp', 'pppp', 'pppp', 'pppp', 'pppp'], pal: { p: '#e87aa8' }, k: 37 },
];

/* ================= People: drawn the way rome.js draws the Trevi's crowd ================= */
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#cfc8c0'];
const NIGHT = '#140e1c', GLOWC = '#ffd8a0', SHADOW = 'rgba(6,2,10,0.42)', MIXED = {};
/** #rrggbb between two #rrggbb colours (cached) */
function mixC(a, b, t) {
  const k = a + b + t;
  if (!MIXED[k]) { const A = C(a), B = C(b); MIXED[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * t).toString(16).padStart(2, '0')).join(''); }
  return MIXED[k];
}
const box = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
/** a look: the proportions for a height of h px, its colours dimmed for the night (dk) and lit by the floodlights (…L) */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixC(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixC(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixC(top, NIGHT, dk + 0.3),
    topL: mixC(top, GLOWC, 0.3), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, hatL: o.hat ? mixC(o.hat, GLOWC, 0.35) : null };
}
/**
 * One person, feet on row fy, centred on column x (rome.js's person(), cut down to what the lane needs). Returns the torso's top row.
 * o.view: 0 from behind (facing the Colosseum), 1 facing us, 2 in profile (o.dir: 1 right, -1 left); o.step: profile stride frame;
 * o.lift: from behind or in front, the foot that is up (1 left, 2 right); o.aL / o.aR: -1 drawn by the caller, 0 down,
 * 1 straight up, 3 out, 5 hand to the face; o.lean: head px sideways; o.nod: head 1 px down; o.look: from behind, the head
 * turned to that side (-1 / 1); o.rim: facing us, the glow behind still catches the shoulders.
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2, legs = P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh + (o.nod ? 1 : 0), bw = side ? tw - 2 : tw, tx = x - (bw >> 1), ly = fy - legs + 1;
  const legC = P.skirt ? P.skin : P.bot;
  box(tx - 1, fy + 1, bw + 2, 1, SHADOW);                                       // contact shadow on the cobbles
  if (side) {
    if (o.step && legs >= 4) {
      box(x - 2, ly, 4, 2, legC);
      box(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); box(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
      box(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); box(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
    } else { box(x - 1, ly, 2, legs, legC); box(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    if (P.skirt) box(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
  } else {
    const lw = bw >= 7 ? 3 : 2, up = legs >= 4 ? o.lift || 0 : 0;
    box(tx, ly, bw, Math.min(2, legs), P.bot);
    if (legs > 2) { box(tx, ly + 2, lw, legs - 2 - (up === 1), legC); box(tx + bw - lw, ly + 2, lw, legs - 2 - (up === 2), legC); }
    box(tx, fy - (up === 1 ? 1 : 0), lw, 1, P.shoe); box(tx + bw - lw, fy - (up === 2 ? 1 : 0), lw, 1, P.shoe);
    if (P.skirt) box(tx, ly, bw, (legs >> 1) + 1, P.skirt);
  }
  box(tx, ty, bw, th, P.top);
  if (!side) box(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1 || o.rim) box(tx, ty, bw, 1, P.topL);                          // the floodlit monument's glow on the shoulders
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); box(ax, ty + 1, 1, th - 2, P.topD); box(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 3) { box(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); box(x + dir * 4, ty + 2, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (st < 0) return;
      if (!st) { box(ax, ty + 1, 1, th - 2, P.topD); box(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { box(ax, hy - 1, 1, ty - hy + 2, P.topD); box(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 3) { box(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); box(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else { box(ax, ty, 1, 2, P.topD); box(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    };
    arm(tx - 1, o.aL || 0, -1); arm(tx + bw, o.aR || 0, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    box(hx, hy, hw, hh, P.hair); box(hx, hy, hw, 1, P.hairL);
    if (P.long) box(hx, hy + hh, hw, P.long, P.hair); else box(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
    if (o.look) box(o.look < 0 ? hx : hx + hw - 1, hy + 1, 1, hh - 2, P.skin);
  } else if (view === 1) {
    box(hx, hy, hw, hh, P.skin); box(hx, hy, hw, hr, P.hair);
    if (P.long) { box(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); box(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { box(hx, hy, hw, hh, P.skin); box(hx, hy, hw, hr, P.hair); box(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) {
    box(hx, hy - 1, hw, 2, P.hat); box(hx, hy - 1, hw, 1, P.hatL);
    if (side) box(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); else box(hx - 1, hy, hw + 2, 1, P.hat);
  }
  return ty;
}

/* ================= Timed life (loop seconds) ================= */
const LANT_FLICK = [[101, 102.4], [171.5, 172.3]];                     // the garden house's lantern stutters
const NEON_FLICK = [[47.3, 48.5], [133, 134.6], [205.2, 206]];
const TOASTS = [[57, 59.5], [131, 133.2], [214, 216.4]];                // table A raises a glass
const WAITER = [                                                       // [t, x] keyframes of each trip out of the door
  [[26, 80], [33, 44], [38.5, 44], [45, 80]], [[140, 80], [143.5, 94], [150, 94], [153.5, 80]], [[200, 80], [203, 74], [209, 74], [212, 80]],
];
const PASSERS = [{ t0: 70, t1: 83, x0: 150, x1: 310, y: 229, P: look(14, 1, 0, '#5a6a9a', '#2a2a34', { dk: 0.32 }) },
  { t0: 158, t1: 170, x0: 308, x1: 152, y: 231, P: look(15, 2, 2, '#a84a5a', '#33466e', { long: 1, dk: 0.32 }) }];
// The lane's people (drawLane):
//     0-240  the accordion player by the trattoria plays; he rests the bellows and wipes his brow at 96-104 and 176-183
//    12-26   a couple come up the lane from below, arm in arm, and stop by him to listen
//    35      he tosses a coin into the open case; the player nods        44  a shooting star: he points up at it
//    50-120  they stroll on up the lane and fade at the road             70-83, 158-170  passers-by cross down the lane
//   128-171  a signora and her terrier step out of the garden gate; the ginger cat looks up as they pass (134-145); the dog
//            sniffs at the gutter (146-150); off to the left
//   190-234  back from the left, a sniff at the string lights' post (199.5-203.5), past the cat (219-229) and in at the gate
const BUSK_BREAKS = [[96, 104], [176, 183]];
const STROLL = [[12, 20.5, -2.0], [26, 32.3, -3.2], [50, 32.3, -3.2], [120, 92, -1.0]];   // [t, m down the lane, m off the gutter]
const COIN = { t: 35, land: 35.9 }, POINT = [44, 47.5];
const DOGWALK = [                                      // [t, x, feet row, what they do until the next key]
  [[128, 466, 253, 'in'], [130, 462, 254, 'walk'], [146, 250, 257, 'sniff'], [150, 250, 257, 'walk'], [171, -16, 258]],
  [[190, -16, 258, 'walk'], [199.5, 98, 258, 'sniff'], [203.5, 98, 258, 'walk'], [232, 452, 254, 'out'], [234.5, 452, 254]],
];
const CAT_ALERT = [[134, 145], [219, 229]];

/* ================= Per-frame: lanterns and lamp pools ================= */
function lantern(l, lv, small) {
  if (small) {
    rect(ctx, l.x - 1, l.y - 3, 3, 1, '#1a1216'); rect(ctx, l.x - 1, l.y - 2, 3, 3, lv > 0.5 ? '#ffe0a0' : '#6a4a30'); rect(ctx, l.x, l.y + 1, 1, 1, '#1a1216');
    halo(HL.lampM, l.x, l.y, lv); halo(HL.lampCore, l.x, l.y, lv * 0.6);
    return;
  }
  rect(ctx, l.x - 1, l.y - 6, 3, 1, '#1a1216'); rect(ctx, l.x - 2, l.y - 5, 5, 1, '#2a2024');
  rect(ctx, l.x - 2, l.y - 4, 5, 6, lv > 0.5 ? '#ffe2a4' : '#7a5838');
  if (lv > 0.5) rect(ctx, l.x - 1, l.y - 3, 3, 4, '#fff6dc');
  rect(ctx, l.x, l.y - 4, 1, 6, '#3a2a24'); rect(ctx, l.x - 2, l.y + 2, 5, 1, '#2a2024'); rect(ctx, l.x, l.y + 3, 1, 1, '#1a1216');
  halo(HL.lamp, l.x, l.y, lv); halo(HL.lampCore, l.x, l.y - 1, lv);
}
const lampLevel = (t, k, ev, salt) => (0.95 + 0.05 * wave(t, k, salt * 0.1)) * (ev ? flick(t, ev, salt) : 1);
function drawPools(t, lvL, lvR, lvM) {
  drawPool(POOL.l, LANT_L.x + 4, 250, lvL); drawPool(POOL.r, LANT_R.x - 6, 246, lvR); drawPool(POOL.m, LANT_M.x + 6, 227, lvM);
  drawPool(POOL.tr, 50, 255, 0.85 + 0.1 * wave(t, 700));
  drawPool(POOL.bar, 332, 230, 0.8 * flick(t, NEON_FLICK, 55));
}

/* ================= Per-frame: down the lane ================= */
function drawMid(t, pitch, board, lvM) {
  blit(L2C); for (const w of L2C.wins) drawWindowLight(w, t);
  const R = board ? R2B : R2N;
  blit(R); for (const w of R.wins) drawWindowLight(w, t);
  const nl = flick(t, NEON_FLICK, 55) * (0.9 + 0.1 * wave(t, 2200, 0.3));
  text(ctx, 'BAR', 319, 189, nl > 0.5 ? '#ff7aa8' : '#4a1a2a');
  halo(HL.neon, 324, 191, nl);
  // two friends chatting outside the bar, one talking with his hands
  const g = wave(t, 37, 0.2) > 0.3;
  rect(ctx, 337, 215, 3, 9, '#2a2230'); rect(ctx, 337, 212, 3, 3, '#1a1210'); rect(ctx, 339, 213, 1, 2, '#c88a60'); rect(ctx, 337, 224, 1, 2, '#141018'); rect(ctx, 339, 224, 1, 2, '#141018');
  rect(ctx, 344, 214, 3, 10, '#4a2a2a'); rect(ctx, 344, 211, 3, 3, '#2a1a12'); rect(ctx, 344, 212, 1, 2, '#c88a60'); rect(ctx, 344, 224, 1, 2, '#141018'); rect(ctx, 346, 224, 1, 2, '#141018');
  rect(ctx, 342, g ? 214 : 217, 2, 1, '#c88a60');
  lantern(LANT_M, lvM, true);
  if (board) drawBillboard(t, pitch);
}

/* ================= The lane's people: an accordion player, a strolling couple, a signora and her terrier ================= */
// The lane's ground row d px below GY is LANE_M / d metres away; there a metre across is LANE_PX·d px and a person 0.33·d px tall.
const LANE_M = 2165, LANE_PX = 0.194;
function drawPasser(t, q) {
  person(lerp(q.x0, q.x1, (t - q.t0) / (q.t1 - q.t0)), q.y, q.P, { view: 2, dir: q.x1 > q.x0 ? 1 : -1, step: Math.floor(t * 3.2) & 1 });
}

/** the accordion player by the trattoria: he plays all night, swaying, tapping a foot, the case open at his feet */
const BUSK = { x: 168, fy: 250 };
const BUSK_P = look(21, 1, 5, '#e8e2d6', '#2c2a36', { hat: '#3c3644', dk: 0.26 });
const ACC = { red: mixC('#b42a3c', NIGHT, 0.18), lit: mixC('#f0707a', NIGHT, 0.12), dark: mixC('#6a1424', NIGHT, 0.2),
  key: mixC('#f0e8d8', NIGHT, 0.2), fold: mixC('#d0c0a4', NIGHT, 0.28), crease: '#1e1418', velvet: mixC('#8a1a30', NIGHT, 0.35) };
function drawBusker(t) {
  const { x, fy } = BUSK, P = BUSK_P, rest = inEvent(t, BUSK_BREAKS), wipe = BUSK_BREAKS.some(([a]) => t >= a + 2.5 && t < a + 4.5);
  const open = rest ? 0 : 0.5 + 0.5 * wave(t, 120), tap = !rest && frac(t * 1.5) < 0.22;
  const sway = rest ? 0 : Math.round(wave(t, 60, 0.1) * 0.7), nod = t >= COIN.land + 0.2 && t < COIN.land + 1;
  // the case, its lid up behind it, coins catching the lamps in the velvet
  box(x + 5, fy - 4, 9, 3, '#2a1a16'); box(x + 5, fy - 4, 9, 1, '#4a3024');
  box(x + 5, fy - 1, 9, 2, '#1e1210'); box(x + 6, fy - 1, 7, 1, ACC.velvet);
  for (const [i, k] of [[7, 0], [10, 1], [11, 2]]) box(x + i, fy - 1, 1, 1, hash(k, step(t, 480), 6) > 0.8 ? '#ffe89a' : '#b08c48');
  const ty = person(x, fy, P, { view: 1, aL: -1, aR: wipe ? 5 : -1, lean: sway, nod, lift: tap ? 1 : 0, rim: true });
  box(x - (P.hw >> 1) + sway + 1, ty - P.hh + (nod ? 1 : 0) + 2, 2, 1, '#6a5a54');            // grey moustache
  // the accordion across his chest: the keys edge on (his right hand), bellows breathing, the bass box (his left hand)
  const kx = x - (P.tw >> 1) - 2, bel = 1 + Math.round(open * 3), bx = kx + 3 + bel;
  box(kx, ty + 1, 1, 6, ACC.key); box(kx, ty + 2, 1, 1, '#1a1418'); box(kx, ty + 4, 1, 1, '#1a1418');
  box(kx + 1, ty + 1, 2, 6, ACC.red); box(kx + 1, ty + 1, 2, 1, ACC.lit); box(kx + 2, ty + 6, 1, 1, ACC.dark);
  for (let i = 0; i < bel; i++) box(kx + 3 + i, ty + 1, 1, 6, i & 1 ? ACC.crease : ACC.fold);
  box(bx, ty + 1, 3, 6, ACC.red); box(bx, ty + 1, 3, 1, ACC.lit); box(bx + 2, ty + 2, 1, 5, ACC.dark);
  box(bx + 1, ty + 3, 1, 1, ACC.key); box(bx + 1, ty + 5, 1, 1, ACC.key);
  box(kx - 1, ty + 1, 1, 3, P.topD); box(kx - 1, ty + 4, 1, 1, P.skin);
  if (!wipe) { box(bx + 3, ty + 1, 1, 3, P.topD); box(bx + 3, ty + 4, 1, 1, P.skin); }
}

/** the couple: up from below arm in arm, a stop by the music (a coin, a shooting star), then on up the lane to the road */
const STROLL_P = [];                                   // [him, her] for each height
for (let h = 6; h <= 34; h++) STROLL_P[h] = [look(h, 0, 1, '#a8c4e0', '#c8b898', { dk: 0.28 }),
  look(h - 1, 1, 3, '#e0645a', '#e0645a', { skirt: '#e0645a', long: Math.max(1, Math.round(h * 0.13)), dk: 0.28 })];
function strollAt(t) {
  const K = STROLL;
  if (t < K[0][0] || t >= K[K.length - 1][0]) return null;
  let k = 0;
  while (t >= K[k + 1][0]) k++;
  const [ta, Za, Xa] = K[k], [tb, Zb, Xb] = K[k + 1], s = (t - ta) / (tb - ta), d = LANE_M / lerp(Za, Zb, s);
  return { fy: GY + d, x: VP.x + lerp(Xa, Xb, s) * LANE_PX * d, h: clamp(Math.round(d * 0.33), 6, 34), walking: Za !== Zb };
}
function drawStroll(t, S) {
  const [him, her] = STROLL_P[S.h], stride = Math.floor(t * 3.2) & 1, big = S.h >= 12;
  const point = t >= POINT[0] && t < POINT[1], listen = !S.walking && !point && t >= STROLL[1][0] + 0.6;
  const toss = t >= COIN.t - 0.6 && t < COIN.t + 0.3, sep = Math.max(2, Math.round(S.h * 0.36)), xh = Math.round(S.x - sep / 2), xs = xh + sep;
  ctx.globalAlpha = clamp((STROLL[STROLL.length - 1][0] - t) / 6, 0, 1);         // they fade as they reach the road
  const tyH = person(xh, S.fy, him, { view: 0, lift: S.walking ? 1 + stride : 0, aL: point ? 1 : toss ? 3 : 0, aR: big ? -1 : 0, look: listen ? -1 : 0 });
  person(xs, S.fy, her, { view: 0, lift: S.walking ? 2 - stride : 0, aL: big ? -1 : 0, lean: listen && t > STROLL[1][0] + 3 ? -1 : 0, look: listen ? -1 : 0 });
  if (big) {                                                                   // arm in arm: his elbow, her forearm through it
    const ax = xh - (him.tw >> 1) + him.tw, ey = tyH + Math.round(him.th * 0.55);
    box(ax, tyH + 1, 1, ey - tyH - 1, him.topD);
    box(ax - 1, ey, xs - (her.tw >> 1) - ax + 2, 1, her.skin);
  }
  ctx.globalAlpha = 1;
  if (t >= COIN.t && t < COIN.land) {                                          // the coin's arc into the open case
    const u = (t - COIN.t) / (COIN.land - COIN.t), hx = xh - (him.tw >> 1) - 4;
    box(Math.round(lerp(hx, BUSK.x + 9, u)), Math.round(lerp(tyH + 1, BUSK.fy - 1, u) - Math.sin(Math.PI * u) * 7), 1, 1, '#ffe89a');
  } else if (t >= COIN.land && t < COIN.land + 0.5) { box(BUSK.x + 9, BUSK.fy - 2, 1, 1, '#ffffff'); halo(HL.spark, BUSK.x + 9, BUSK.fy - 2, 0.6); }
}

/** the signora and her terrier: out of the garden gate, off to the left, and back in (a sniff on each way) */
const WALKER_P = look(23, 0, 5, '#dcd0b8', '#2c3654', { skirt: '#2c3654', dk: 0.3 });
const LEAD = 13, LEAD_C = mixC('#c8303c', NIGHT, 0.25);               // the dog trots this far ahead of her (px)
const DOG_PAL = { w: mixC('#ece6da', NIGHT, 0.26), W: mixC('#fff2dc', NIGHT, 0.1), t: mixC('#c8843c', NIGHT, 0.26), e: '#120c10', n: '#120c10',
  c: '#c82a3a', f: mixC('#9a9086', NIGHT, 0.3) };
const DOG_ROWS = [                                     // facing right, paws on the last row
  ['......t...', '......ttt.', '......tewn', '.WWWWWcww.', '.wttwwwww.', '.w.w..w.w.', '.f.f..f.f.'],   // trotting, legs apart
  ['......t...', '......ttt.', '......tewn', '.WWWWWcww.', '.wttwwwww.', '..ww..ww..', '..ff..ff..'],   // trotting, legs gathered
  ['..........', '..........', '..........', '.WWWWWct..', '.wttwwwte.', '.w.w..w.wn', '.f.f..f...'],   // nose to the ground
];
function dogWalkAt(t) {
  for (let i = 0; i < DOGWALK.length; i++) {
    const trip = DOGWALK[i];
    if (t < trip[0][0] || t >= trip[trip.length - 1][0]) continue;
    let k = 0;
    while (t >= trip[k + 1][0]) k++;
    const [ta, xa, ya, mode] = trip[k], [tb, xb, yb] = trip[k + 1], s = (t - ta) / (tb - ta);
    return { x: Math.round(lerp(xa, xb, s)), fy: Math.round(lerp(ya, yb, s)), dir: i ? 1 : -1, mode, s };
  }
  return null;
}
function drawDogWalk(t, D) {
  const { x, fy, dir, mode } = D, walk = mode === 'walk', P = WALKER_P;
  ctx.globalAlpha = mode === 'in' ? D.s : mode === 'out' ? 1 - D.s : 1;            // through the garden gate
  const sx = x + dir * LEAD - 5, sy = fy - 6, spr = DOG[mode === 'sniff' ? 2 : walk ? Math.floor(t * 6.4) & 1 : 0][dir > 0 ? 0 : 1];
  const ty = fy - P.ll - P.th + 1, hx = x + dir * 2, hy = ty + 6, cx = sx + (dir > 0 ? 6 : 3), n = Math.abs(cx - hx), sag = walk ? 0.6 : 1.4;
  // the lead from her hand to the collar, under the dog so it meets the collar over its back; slack while they stand
  for (let i = 1; i <= n; i++) box(hx + dir * i, Math.round(lerp(hy, sy + 2, i / n) + sag * Math.sin(Math.PI * i / n)), 1, 1, LEAD_C);
  box(sx + 1, fy + 1, 9, 1, SHADOW);
  ctx.drawImage(spr, sx, sy);
  const wag = Math.floor(t * (mode === 'sniff' ? 8 : 4)) & 1, tb = dir > 0 ? sx : sx + 9;
  box(tb, sy + 2, 1, 1, DOG_PAL.w); box(tb - dir * wag, sy + 1, 1, 1, DOG_PAL.w);  // tail up, wagging
  person(x, fy, P, { view: 2, dir, step: walk && (Math.floor(t * 3.2) & 1), aR: -1 });
  box(x + dir, ty + 1, 1, 3, P.topD); box(x + dir * 2, ty + 4, 1, 2, P.topD); box(hx, hy, 1, 1, P.skin);   // her arm, down and forward
  ctx.globalAlpha = 1;
}

/** everyone in the lane, nearer feet over further ones */
const LANE_DL = [];
function drawLane(t) {
  const S = strollAt(t), D = dogWalkAt(t);
  LANE_DL.length = 0;
  PASSERS.forEach((q, i) => { if (t >= q.t0 && t < q.t1) LANE_DL.push([q.y, i]); });
  LANE_DL.push([BUSK.fy, -1]);
  if (S) LANE_DL.push([S.fy, -2]);
  if (D) LANE_DL.push([D.fy, -3]);
  LANE_DL.sort((a, b) => a[0] - b[0]);
  for (const [, id] of LANE_DL) {
    if (id >= 0) drawPasser(t, PASSERS[id]);
    else if (id === -1) drawBusker(t);
    else if (id === -2) drawStroll(t, S);
    else drawDogWalk(t, D);
  }
}

/* ================= Per-frame: the trattoria ================= */
function drawWaiter(t) {
  for (const trip of WAITER) {
    if (t < trip[0][0] || t >= trip[trip.length - 1][0]) continue;
    let k = 0;
    while (t >= trip[k + 1][0]) k++;
    const [ta, xa] = trip[k], [tb, xb] = trip[k + 1], x = Math.round(lerp(xa, xb, (t - ta) / (tb - ta)));
    const moving = xa !== xb, dir = moving ? Math.sign(xb - xa) : -1, fy = 246, st = moving && (Math.floor(t * 4) & 1);
    const R = (i, j, w, h, c) => rect(ctx, x + (dir > 0 ? i : -i - w + 1), fy + j, w, h, c);
    R(st ? -1 : 0, -3, 1, 3, '#141014'); R(st ? 1 : 0, -3, 1, 3, '#141014');
    R(-1, -10, 3, 7, '#f0ece4'); R(-1, -10, 1, 7, '#b8b0a4');                                   // long white apron
    R(-1, -15, 3, 5, '#1a1418'); R(0, -15, 1, 3, '#f4f0e8');                                     // black waistcoat, white shirt
    R(0, -18, 2, 3, '#e0a070'); R(-1, -18, 1, 2, '#1a1210'); R(0, -19, 2, 1, '#1a1210');
    R(2, -15, 1, 1, '#f4f0e8'); R(2, -17, 1, 2, '#e0a070');                                      // arm up, carrying the tray
    R(1, -18, 4, 1, '#c8ccd8'); R(2, -20, 1, 2, '#8a1a2a'); R(4, -19, 1, 1, '#f0e0a0');
  }
}
function drawLeft(t, lvL) {
  blit(L1C);
  for (const w of L1C.wins) {
    if (w.kind !== 'watch') { drawWindowLight(w, t); continue; }
    // someone at the window, looking up for shooting stars; they point when one falls
    rect(ctx, w.x, w.y, w.w, w.h, '#f4a852'); rect(ctx, w.x, w.y, w.w, 5, '#ffd08a'); rect(ctx, w.x + w.w - 2, w.y, 2, w.h, '#ffe6b8');
    const up = METEORS.some(m => t >= m.t && t < m.t + 3.5), hx = w.x + 6;
    rect(ctx, hx - 3, w.y + 11, 8, 9, '#2a1a1c'); rect(ctx, hx - 1, w.y + (up ? 5 : 6), 4, 5, '#2a1a1c'); rect(ctx, hx - 2, w.y + 18, 10, 2, '#3a2420');
    if (up) for (let k = 0; k < 7; k++) rect(ctx, hx + 4 + k, w.y + 11 - k, 1, 1, '#2a1a1c');
    rect(ctx, w.x + (w.w >> 1), w.y, 1, 2, '#6a4230');
    halo(HL.win, w.x + w.w / 2, w.y + w.h / 2, 0.9);
  }
  lantern(LANT_L, lvL);
  drawWaiter(t);
  blit(TABLES_C);
  TABLES.forEach((tb, i) => {                                                   // candles in their jars
    const hv = hash(i, Math.floor(t * 10), 21), tall = hv > 0.3;
    rect(ctx, tb.x, tb.y - (tall ? 4 : 3), 1, tall ? 2 : 1, hv > 0.8 ? '#ffffff' : '#fff0a0'); rect(ctx, tb.x + 1, tb.y - 3, 1, 1, '#ffb040');
    halo(HL.candle, tb.x, tb.y - 3, 0.75 + 0.25 * hv);
  });
  const toast = TOASTS.find(([a, b]) => t >= a && t < b);
  if (toast) {                                                                  // cin cin: two glasses meet over table A
    const s = (t - toast[0]) / (toast[1] - toast[0]), up = Math.round(Math.sin(Math.PI * Math.min(1, s * 1.4)) * 4), tb = TABLES[0];
    for (const sd of [-1, 1]) { rect(ctx, tb.x + sd * 5, tb.y - 2 - up, 1, 1, '#8a1a2a'); rect(ctx, tb.x + sd * 5, tb.y - 1 - up, 1, 1, '#d0c8d8'); }
    if (s > 0.35 && s < 0.5) { rect(ctx, tb.x, tb.y - 7, 1, 1, '#ffffff'); halo(HL.spark, tb.x, tb.y - 7, 0.8); }
  }
  BULBS.forEach((b, i) => {                                                     // string lights over the tables
    const lv = 0.82 + 0.18 * wave(t, 400 + i * 17, i * 0.13);
    rect(ctx, b.x, b.y, 1, 2, lv > 0.9 ? '#fff4c8' : '#ffd88a'); rect(ctx, b.x, b.y - 1, 1, 1, '#3a2a20');
    halo(HL.bulb, b.x, b.y + 1, lv);
  });
}

/* ================= Per-frame: the garden house, the nasone, the cats ================= */
function drawRight(t, lvR) {
  blit(R1C);
  for (const w of R1C.wins) drawWindowLight(w, t);
  const S = SHRINE, vf = 0.8 + 0.2 * hash(5, Math.floor(t * 8), 3);
  rect(ctx, S.x + 6, S.y + 26, 2, 1, vf > 0.9 ? '#ff8a6a' : '#e0302a'); halo(HL.votive, S.x + 7, S.y + 26, vf);
  halo(HL.shrine, S.x + 7, S.y + 14, 0.8);
  lantern(LANT_R, lvR);
  ctx.drawImage(NAS_C, NAS.x - 5, NAS.y);
  // the trickle: highlights run down the stream, droplets hop out of the grate, rings spread in the puddle
  const ph = Math.floor(t * 24);
  for (let y = 237; y < 247; y++) { ctx.globalAlpha = (y + ph) % 4 === 0 ? 0.95 : 0.55; rect(ctx, 363, y, 1, 1, (y + ph) % 4 === 0 ? '#e8f4ff' : '#8ab4dc'); }
  ctx.globalAlpha = 1;
  for (let k = 0; k < 2; k++) {
    const { cyc, u } = cycle(t, 900 + k * 97, k * 0.5), dx = (hash(k, cyc, 5) < 0.5 ? -1 : 1) * (1 + Math.round(u * 2));
    rect(ctx, 363 + dx, 246 - Math.round(Math.sin(Math.PI * u) * 2), 1, 1, '#c8e0f8');
  }
  { const { u } = cycle(t, 400), rr = 1 + u * 7; ctx.globalAlpha = (1 - u) * 0.5; ctx.fillStyle = '#d0e4ff';
    for (let j = 0; j < 12; j++) { const a = j / 12 * TAU; ctx.fillRect(Math.round(356 + Math.cos(a) * rr), Math.round(251 + Math.sin(a) * rr * 0.28), 1, 1); }
    ctx.globalAlpha = 1; }
  rect(ctx, 355, 251, 3, 1, '#ffe0a0');                                         // the lantern caught in the puddle
  ctx.drawImage(VESPA_C, VESPA.x, VESPA.y);
  // cat on the garden wall: tail swings down the wall, eyes blink
  const cx = 444, cy = 188;
  rect(ctx, cx, cy + 1, 7, 7, '#0e0a10'); rect(ctx, cx + 1, cy - 3, 5, 4, '#0e0a10'); rect(ctx, cx + 1, cy - 4, 1, 1, '#0e0a10'); rect(ctx, cx + 5, cy - 4, 1, 1, '#0e0a10');
  rect(ctx, cx + 6, cy + 1, 1, 6, '#3a2a28');
  if (!(frac(t / 5) > 0.95 || frac(t / 48) > 0.985)) { rect(ctx, cx + 2, cy - 2, 1, 1, '#c8f060'); rect(ctx, cx + 4, cy - 2, 1, 1, '#c8f060'); }
  const sw = wave(t, 32, 0.1) * 1.2 + wave(t, 90, 0.4) * 0.6;
  for (let j = 0; j < 10; j++) rect(ctx, cx + 7 + Math.round(sw * j / 9), cy + 6 + j, 1, 1, '#0e0a10');
  // a ginger cat by the puddle, stooping to drink now and then; she looks up, tail bristling, while the terrier trots by
  const alert = inEvent(t, CAT_ALERT), drink = !alert && frac(t / 30) > 0.78, bx = 344, by = 249;
  rect(ctx, bx, by - 5, 6, 5, '#b8642a'); rect(ctx, bx, by - 5, 6, 1, '#e08a40'); rect(ctx, bx + 1, by - 3, 1, 3, '#7a3a1a'); rect(ctx, bx + 3, by - 4, 1, 3, '#7a3a1a');
  const hy = drink ? by - 3 : by - (alert ? 9 : 8), hx = bx + (drink ? 6 : 5);
  rect(ctx, hx, hy, 3, 3, '#c8702e'); rect(ctx, hx, hy - 1, 1, 1, '#c8702e'); rect(ctx, hx + 2, hy - 1, 1, 1, '#c8702e'); rect(ctx, hx, hy, 3, 1, '#e8964a');
  if (!drink && (alert || frac(t / 8) < 0.93)) rect(ctx, hx + 2, hy + 1, 1, 1, '#1a1210');
  if (alert) { rect(ctx, bx - 2, by - 10, 2, 7, '#b8642a'); rect(ctx, bx - 2, by - 10, 1, 7, '#e08a40'); rect(ctx, bx - 1, by - 11, 1, 1, '#b8642a'); }
  else {
    const tsw = Math.round(wave(t, 20, 0.6) * 1.5);
    rect(ctx, bx - 1, by - 2, 1, 2, '#b8642a'); rect(ctx, bx - 2, by - 4 + tsw, 1, 3, '#b8642a');
  }
}

/* ================= Per-frame: laundry swaying on the line ================= */
function drawLaundry(t) {
  blit(WIRE_C);
  for (const c of CLOTHES) {
    const y0 = Math.round(wireY(c.x)), sw = wave(t, c.k, c.x * 0.01) * 0.9 + wave(t, c.k * 3, 0.3) * 0.3;
    c.rows.forEach((row, j) => {
      const off = Math.round(sw * j / c.rows.length * 1.6);
      for (let i = 0; i < row.length; i++) if (row[i] !== '.') {
        const base = c.pal[row[i]], shade = i === 0 || j === c.rows.length - 1;
        rect(ctx, c.x + i + off, y0 + 1 + j, 1, 1, shade ? CLOTH_SHADE[base] : base);
      }
    });
    rect(ctx, c.x, y0, 1, 2, '#6a5a4a'); rect(ctx, c.x + c.rows[0].length - 1, y0, 1, 2, '#6a5a4a');   // pegs
  }
}
const CLOTH_SHADE = {};

/* ================= Bougainvillea bracts: papery magenta trefoils tumbling and flipping at three depths ================= */
const BRACT_DEPTHS = [
  { count: 26, nMin: 5, nMax: 8, amp: 3, drift: [60, 140], land: [184, 204], tree: 0 },
  { count: 16, nMin: 7, nMax: 10, amp: 5, drift: [120, 240], land: [212, 250], tree: 0 },
  { count: 13, nMin: 9, nMax: 12, amp: 8, drift: [200, 330], land: [H + 8, H + 8], tree: 0.6 },
];
const BRACTS = BRACT_DEPTHS.map((d, depth) => {
  const r = rng(990 + depth), out = [];
  for (let i = 0; i < d.count; i++) out.push({
    id: depth * 100 + i, n: d.nMin + Math.floor(r() * (d.nMax - d.nMin + 1)), ph: r(),
    ks: 30 + Math.floor(r() * 40), p2: r(), kr: 16 + Math.floor(r() * 30), p3: r(), kf: 24 + Math.floor(r() * 44), p4: r(),
    amp: d.amp * (0.7 + r() * 0.6), drift: lerp(d.drift[0], d.drift[1], r()), col: Math.floor(r() * 3),
  });
  return out;
});
const BRACT_PAL = [
  [['#8a1654', '#d8288a', '#ff6ab8'], ['#a8306e', '#e868ae', '#ffa2d0']],
  [['#9a1a62', '#ec3c9c', '#ff86c8'], ['#b8407e', '#f47cbc', '#ffb6da']],
  [['#6e1250', '#b8207a', '#ee4aa4'], ['#8a2a66', '#cc5a9a', '#f08cc4']],
];
function makeBract(size, pal, back) {
  const s = size === 1 ? 5 : 7, c = (s - 1) / 2, d = size === 1 ? 1.2 : 1.8, rr = size === 1 ? 1.25 : 1.75, p = pix(s, s);
  const lobes = [-Math.PI / 2, Math.PI / 6, Math.PI * 5 / 6].map(a => [c + Math.cos(a) * d, c + Math.sin(a) * d]);
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    let v = 0, L = null;
    for (const l of lobes) { const w = 1 - ((x - l[0]) ** 2 + (y - l[1]) ** 2) / (rr * rr); if (w > v) { v = w; L = l; } }
    if (!L) continue;
    p.set(x, y, v < 0.3 ? pal[0] : (x - L[0]) + (y - L[1]) < -0.4 ? pal[2] : pal[1]);
  }
  p.set(c, c, back ? pal[2] : '#fff0c8');
  return p.done();
}
function drawBracts(depth, t) {
  const D = BRACT_DEPTHS[depth];
  for (const b of BRACTS[depth]) {
    const { cyc, u } = cycle(t, b.n, b.ph), land = lerp(D.land[0], D.land[1], hash(b.id, cyc, 4));
    let x0, y0;
    if (hash(b.id, cyc, 3) < D.tree) { x0 = 392 + hash(b.id, cyc, 1) * 66; y0 = 146 + hash(b.id, cyc, 2) * 70; }   // shed by the bougainvillea
    else { x0 = 120 + hash(b.id, cyc, 1) * 420; y0 = depth === 0 ? 96 + hash(b.id, cyc, 2) * 30 : -8 - hash(b.id, cyc, 2) * 12; }
    const sw = wave(t, b.ks, b.p2), x = x0 - u * b.drift + sw * b.amp, y = y0 + u * (land - y0) - Math.abs(sw) * (depth + 1) * 0.8;
    if (x < -8 || x > W + 8) continue;
    const a = Math.min(1, u / (depth === 0 ? 0.25 : 0.05), D.land[0] < H ? (1 - u) / 0.12 : 1);
    const fl = Math.cos(TAU * (b.kf * t / LOOP + b.p4)), back = fl < 0, pal = BRACT_PAL[b.col][back ? 1 : 0];
    if (depth === 0) {
      ctx.globalAlpha = a * 0.9; ctx.fillStyle = pal[1];
      ctx.fillRect(Math.round(x), Math.round(y), Math.abs(fl) > 0.5 ? 2 : 1, 1);
      continue;
    }
    const spr = BRACT_SPR[depth][b.col][back ? 1 : 0], hw = spr.width >> 1;
    ctx.save();
    ctx.globalAlpha = a;
    ctx.translate(Math.round(x), Math.round(y)); ctx.rotate(sw * 1.1 + 0.9 * wave(t, b.kr, b.p3)); ctx.scale(1, Math.max(0.25, Math.abs(fl)));
    ctx.drawImage(spr, -hw, -hw);
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

/* ================= A picked weather (site/js/engine/weather.js) ================= */
// Rome draws its own bracts (only with its own weather); rain, snow and leaves land on the street at the Colosseum's feet,
// on the lane and past the bottom of the frame, and catch the lanterns, the string lights, the floodlights and the neon
const WEATHER = weatherPlans(['bracts']);
const WX_LAND = [[184, 204], [212, 250], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: LANT_L.x, y: LANT_L.y + 22, rx: 42, ry: 58 }, { x: LANT_R.x, y: LANT_R.y + 22, rx: 42, ry: 58 },
  { x: LANT_M.x, y: LANT_M.y + 12, rx: 20, ry: 28 }, { x: 60, y: 206, rx: 64, ry: 26, col: '#ffd890' },
  { x: CO.cx, y: 170, rx: 84, ry: 36, col: '#ffc070' }, { x: 324, y: 200, rx: 22, ry: 14, col: '#ff8ab0' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + 24, y: BB.y + 10, rx: 34, ry: 22, col: '#ffe8c8' }] };

/* ================= Pre-render everything static ================= */
let HL, SKY_C, CLOUD_C, FAR, CAMP_C, PINES_C, COLO, ROAD_C, VEH, L2C, R2B, R2N, AD, BB_PITCH, GROUND_C, L1C, R1C, NAS_C, VESPA_C,
  TABLES_C, WIRE_C, POOL, BRACT_SPR, VIG_C, DOG;
function prerender() {
  HL = {
    meteor: makeHalo(6, '#cfe0ff', 0.5, 1.2), strobe: makeHalo(4, '#e8f0ff', 0.6, 1), belfry: makeHalo(7, '#ffb060', 0.35),
    colo: makeHalo(116, '#ff9a40', 0.12, 1.4), coloMid: makeHalo(64, '#ffc070', 0.1, 1.5), coloBase: makeHalo(44, '#ffc070', 0.18, 1.5),
    post: makeHalo(9, '#ffd890', 0.45), head: makeHalo(6, '#fff2d0', 0.5), tail: makeHalo(3, '#ff3a2a', 0.5, 1),
    win: makeHalo(14, '#ffb45a', 0.16), winS: makeHalo(10, '#ffb45a', 0.12), tv: makeHalo(14, '#5a8cff', 0.28),
    lamp: makeHalo(62, '#ffd49a', 0.17, 1.7), lampCore: makeHalo(12, '#fff4dc', 0.45, 1.4), lampM: makeHalo(30, '#ffd49a', 0.2, 1.6),
    neon: makeHalo(16, '#ff5a8a', 0.35), bbGlow: makeHalo(34, '#ffe0c0', 0.1), bbLamp: makeHalo(6, '#ffe8c8', 0.5),
    candle: makeHalo(12, '#ffb050', 0.34), bulb: makeHalo(6, '#ffd080', 0.4), spark: makeHalo(4, '#ffffff', 0.6, 1),
    votive: makeHalo(6, '#ff4a2a', 0.45), shrine: makeHalo(10, '#ffe0a0', 0.22),
  };
  SKY_C = renderSky(); CLOUD_C = renderClouds(); FAR = renderFar(); CAMP_C = renderCampanile(); PINES_C = renderPines(PINES);
  COLO = renderColosseum(); ROAD_C = renderRoad(); VEH = CARS.map(renderVehicle);
  L2C = renderL2(); R2B = renderR2(true); R2N = renderR2(false);
  AD = renderAd(); BB_PITCH = makePitch(BB.w, BB.h, { accent: '#f25fd0' });
  GROUND_C = renderGround(); L1C = renderL1(); R1C = renderR1(); NAS_C = renderNasone(); VESPA_C = renderVespa(); TABLES_C = renderTables();
  { const p = pix(W, H); for (let x = WIRE.x0; x <= WIRE.x1; x++) p.set(x, Math.round(wireY(x)), '#1a1418'); p.fill(WIRE.x0 - 1, WIRE.y0 - 1, 2, 3, '#3a2a24'); p.fill(WIRE.x1, WIRE.y1 - 1, 2, 3, '#3a2a24'); WIRE_C = trim(p); }
  for (const c of CLOTHES) for (const k in c.pal) CLOTH_SHADE[c.pal[k]] = mix(c.pal[k], '#241634', 0.45);
  POOL = { l: makePool(44, 9, '#ffc880', 0.3), r: makePool(40, 8, '#ffc880', 0.3), m: makePool(20, 5, '#ffc880', 0.28),
    tr: makePool(70, 12, '#ff9a50', 0.2), bar: makePool(22, 4, '#ff7aa0', 0.22) };
  BRACT_SPR = [null, 1, 2].map(s => s && BRACT_PAL.map(pl => [makeBract(s, pl[0], false), makeBract(s, pl[1], true)]));
  VIG_C = renderVignette();
  DOG = DOG_ROWS.map(rows => { const r = sprite(rows, DOG_PAL); return [r, mirrored(r)]; });
}
function renderVignette() {
  const p = pix(W, H), c = [10, 4, 16];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.7, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.45 * 255));
  }
  return p.done();
}

/* ================= Frame compositor (back to front) ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    const a = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph));
    ctx.fillStyle = s.col; ctx.globalAlpha = a; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.bright) { ctx.globalAlpha = a * 0.3; ctx.fillRect(s.x - 1, s.y, 1, 1); ctx.fillRect(s.x + 1, s.y, 1, 1); ctx.fillRect(s.x, s.y - 1, 1, 1); ctx.fillRect(s.x, s.y + 1, 1, 1); }
  }
  ctx.globalAlpha = 1;
  const off = Math.floor(frac(t / LOOP) * W);
  ctx.drawImage(CLOUD_C, off - W, 146); ctx.drawImage(CLOUD_C, off, 146);
  drawMeteors(t);
  drawPlane(t);
}
function drawFarLayer(t) {
  blit(FAR.c);
  for (const w of FAR.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < 0.55) rect(ctx, w.x, w.y, 1, 1, '#c89060');
  drawCampanile(t);
}
/** render(t) paints the city's own gelato ad; { pitch: true } paints the site's pitch on its face; { billboard: false }
 *  leaves out the hoarding with its scaffold and lamps (the loop videos); { weather } draws a WEATHERS pick */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const lvL = lampLevel(t, 1500, null, 1), lvR = lampLevel(t, 1300, LANT_FLICK, 2), lvM = lampLevel(t, 1700, null, 3);
  drawSky(t);
  drawFarLayer(t);
  if (wx.bracts) drawBracts(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawColosseum(t);
  blit(PINES_C);
  drawGulls(t);
  blit(ROAD_C);
  for (const X of POSTS) halo(HL.post, X, 179, 0.9);
  drawTourists(t);
  drawTraffic(t);
  ctx.drawImage(GROUND_C, 0, 0);
  drawPools(t, lvL, lvR, lvM);
  drawMid(t, pitch, billboard, lvM);
  if (wx.bracts) drawBracts(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawLeft(t, lvL);
  drawRight(t, lvR);
  drawLane(t);
  drawLaundry(t);
  if (wx.bracts) drawBracts(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const COLO_SPAN = (() => {                            // the Colosseum's x span, from the same projection it is drawn with
  const o = ringColumns(1, CO.b, Math.PI, CO.cut), xc = o[o.length - 1].x;
  const inner = ringColumns(CO.a2, CO.b2, Math.PI * 1.3, TAU).filter(c => c.x > xc);
  return [o[0].x, inner[inner.length - 1].x + 1];
})();

export default {
  id: 'rome-colosseum',
  name: 'Rome',
  tagline: 'Summer night by the Colosseum',
  timeZone: 'Europe/Rome',
  accent: '#f25fd0',
  focusX: CO.cx,
  landmark: COLO_SPAN,                                 // the Colosseum, from the full outer wall to the end of the inner ring
  landmarkTop: 130,                                    // the row of the outer wall's rim, which landscape crops keep
  billboard: BB,
  ambience: { cicadas: 0.3, crickets: 0.25, crowd: 0.22, traffic: 0.14, water: 0.1 },
  events: [...BELLS.map(t => ({ t, type: 'bell', x: CAMP.x, gain: 0.7 })), { t: 36, type: 'gull', x: 250 }, { t: 151, type: 'gull', x: 210 }]
    .sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
